import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Settings, Building2, Briefcase, Workflow, Plug, Users as UsersIcon, CalendarClock, BookOpen, X, AlertTriangle, Search, Tag, Store, Plus, Trash2, Building, Key, IdCard, Plane, ShieldCheck, GitBranch, Sparkles, Download, RefreshCw, FileText as FileTextIcon, Bell, Play, Save, Eye, EyeOff, ChevronDown, ChevronRight, Pencil, RotateCcw, UserX, UserCheck, Database } from "lucide-react";
import PayrollMgmt from "./PayrollMgmt";
import QBOMapping from "./QBOMapping";
import PredeployCheck from "../components/PredeployCheck";
import PermissionsMatrix from "../components/PermissionsMatrix";

const Tab = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-[12px] text-[13px] font-medium transition-all whitespace-nowrap ${
      active ? "bg-[#e2e8f0] text-[#475569]" : "text-[#747474] hover:bg-[#f6f6f6]"
    }`}
  >
    <Icon size={15} /> {label}
  </button>
);

const expirationBadge = (days) => {
  if (days == null) return <span className="text-[12px] text-[#747474]">—</span>;
  if (days < 0) return <span className="sw-badge sw-badge-rejected"><AlertTriangle size={11} /> EXPIRED</span>;
  if (days <= 60) return <span className="sw-badge sw-badge-pending"><AlertTriangle size={11} /> {days}d</span>;
  return <span className="sw-badge sw-badge-approved">{days}d</span>;
};

function AdjustBalanceModal({ user, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    pto_balance: user.pto_balance ?? 0,
    vacation_balance: user.vacation_balance ?? 0,
    pto_expires_at: user.pto_expires_at?.split("T")[0] || "",
    vacation_expires_at: user.vacation_expires_at?.split("T")[0] || "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/balance-adjust`, {
        pto_balance: parseFloat(form.pto_balance),
        vacation_balance: parseFloat(form.vacation_balance),
        pto_expires_at: form.pto_expires_at ? `${form.pto_expires_at}T23:59:59+00:00` : null,
        vacation_expires_at: form.vacation_expires_at ? `${form.vacation_expires_at}T23:59:59+00:00` : null,
        reason: form.reason,
      });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">{t("admin.adjust_balance")}</h3>
            <p className="text-[13px] text-[#747474] mt-1">{user.name} · {user.location}</p>
          </div>
          <button onClick={onClose} className="text-[#747474] hover:text-[#323232]">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="sw-label">{t("admin.pto.balance")}</label>
            <input type="number" step="0.5" value={form.pto_balance} onChange={(e) => setForm({ ...form, pto_balance: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.vacation_balance")}</label>
            <input type="number" step="0.5" value={form.vacation_balance} onChange={(e) => setForm({ ...form, vacation_balance: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.expires_at")}</label>
            <input type="date" value={form.pto_expires_at} onChange={(e) => setForm({ ...form, pto_expires_at: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.vacation_expires_at")}</label>
            <input type="date" value={form.vacation_expires_at} onChange={(e) => setForm({ ...form, vacation_expires_at: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">{t("admin.adjust_reason")} *</label>
            <textarea required rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="sw-input" placeholder={t("admin.adjust_reason_ph")} />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="adjust-save-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : t("admin.adjust_save")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PTOManagementTab() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [payrollCompanies, setPayrollCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [filterLoc, setFilterLoc] = useState("all");
  const [filterPC, setFilterPC] = useState("all");
  const [onlyExpiring, setOnlyExpiring] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const load = async () => {
    const [{ data }, { data: rules }] = await Promise.all([
      api.get("/pto/admin/overview"),
      api.get("/pto-rules").catch(() => ({ data: [] })),
    ]);
    setRows(data);
    // distinct list of payroll companies from rules
    const pcs = Array.from(new Set((rules || []).map((r) => r.payroll_company).filter(Boolean))).sort();
    setPayrollCompanies(pcs);
  };
  useEffect(() => { load(); }, []);

  const locations = Array.from(new Set(rows.map((r) => r.location).filter(Boolean)));
  const filtered = rows.filter((r) => {
    if (filterLoc !== "all" && r.location !== filterLoc) return false;
    if (filterPC !== "all" && (r.payroll_company || "") !== filterPC) return false;
    if (q && !`${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (onlyExpiring) {
      const min = Math.min(
        r.pto_days_to_expire ?? 9999,
        r.vacation_days_to_expire ?? 9999,
      );
      if (min > 60) return false;
    }
    return true;
  });

  const totals = filtered.reduce(
    (acc, r) => ({
      pto: acc.pto + (r.pto_balance || 0),
      vac: acc.vac + (r.vacation_balance || 0),
      usedP: acc.usedP + (r.used_pto_year || 0),
      usedV: acc.usedV + (r.used_vacation_year || 0),
    }),
    { pto: 0, vac: 0, usedP: 0, usedV: 0 }
  );

  const handleExport = () => {
    if (!filtered.length) { alert(t("common.nothing_export")); return; }
    const cols = [
      "id", "name", "email", "location", "payroll_company", "contract_type",
      "pto_balance", "pto_expires_at", "pto_days_to_expire",
      "vacation_balance", "vacation_expires_at", "vacation_days_to_expire",
      "used_pto_year", "used_vacation_year",
    ];
    const lines = [cols.join(",")];
    filtered.forEach((r) => {
      const row = cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",");
      lines.push(row);
    });
    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pto_overview_${date}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sw-card">
          <div className="sw-label">{t("admin.pto.total_outstanding")}</div>
          <div className="text-[28px] font-semibold text-[#323232]">{totals.pto.toFixed(1)} <span className="text-[13px] text-[#747474] font-normal">{t("dash.days")}</span></div>
        </div>
        <div className="sw-card">
          <div className="sw-label">{t("admin.pto.total_vacation_outstanding")}</div>
          <div className="text-[28px] font-semibold text-[#323232]">{totals.vac.toFixed(1)} <span className="text-[13px] text-[#747474] font-normal">{t("dash.days")}</span></div>
        </div>
        <div className="sw-card">
          <div className="sw-label">{t("admin.pto.pto_used_year")}</div>
          <div className="text-[28px] font-semibold text-[#475569]">{totals.usedP.toFixed(1)}</div>
        </div>
        <div className="sw-card">
          <div className="sw-label">{t("admin.pto.vac_used_year")}</div>
          <div className="text-[28px] font-semibold text-[#475569]">{totals.usedV.toFixed(1)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="sw-card flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
          <input data-testid="pto-mgmt-search" value={q} onChange={(e) => setQ(e.target.value)} className="sw-input pl-11" placeholder={t("admin.pto.search")} />
        </div>
        <select data-testid="pto-mgmt-filter-loc" value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="sw-input lg:w-[200px]">
          <option value="all">{t("admin.pto.all_locations")}</option>
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select data-testid="pto-mgmt-filter-pc" value={filterPC} onChange={(e) => setFilterPC(e.target.value)} className="sw-input lg:w-[220px]">
          <option value="all">{t("admin.all_contracts")}</option>
          {payrollCompanies.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#f6f6f6] rounded-[10px] cursor-pointer text-[13px] text-[#323232]">
          <input data-testid="filter-expiring" type="checkbox" checked={onlyExpiring} onChange={(e) => setOnlyExpiring(e.target.checked)} className="accent-[#475569]" />
          {t("admin.expiring_soon_filter")}
        </label>
        <button data-testid="pto-export-csv" onClick={handleExport} className="sw-btn-secondary">
          <Download size={14} /> {t("common.export_csv")}
        </button>
      </div>

      {/* Table */}
      <div className="sw-card !p-0 overflow-x-auto">
        <table className="w-full text-[13px] min-w-[900px]">
          <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">{t("common.name")}</th>
              <th className="text-left px-4 py-3">{t("common.location")}</th>
              <th className="text-left px-4 py-3">{t("admin.payroll_company")}</th>
              <th className="text-right px-4 py-3">{t("admin.pto.balance")}</th>
              <th className="text-center px-4 py-3">{t("admin.pto.expires_at")}</th>
              <th className="text-right px-4 py-3">{t("admin.pto.vacation_balance")}</th>
              <th className="text-center px-4 py-3">{t("admin.pto.vacation_expires_at")}</th>
              <th className="text-right px-4 py-3">{t("admin.pto.pto_used_year")}</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`pto-row-${r.id}`} className="border-t border-[#eeeded] hover:bg-[#f6f6f6]">
                <td className="px-4 py-3">
                  <div className="font-medium text-[#323232]">{r.name}</div>
                  <div className="text-[11px] text-[#475569]">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-[#323232]">{r.location}</td>
                <td className="px-4 py-3">
                  {r.payroll_company
                    ? <span className="sw-badge sw-badge-neutral text-[11px]">{r.payroll_company}</span>
                    : <span className="text-[11px] text-[#94a3b8]">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-medium">{r.pto_balance}</td>
                <td className="px-4 py-3 text-center">{expirationBadge(r.pto_days_to_expire)}</td>
                <td className="px-4 py-3 text-right font-medium">{r.vacation_balance}</td>
                <td className="px-4 py-3 text-center">{expirationBadge(r.vacation_days_to_expire)}</td>
                <td className="px-4 py-3 text-right text-[#747474]">{r.used_pto_year} / {r.used_vacation_year}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`adjust-btn-${r.id}`} onClick={() => setEditingUser(r)} className="text-[#475569] hover:bg-[#e2e8f0] px-3 py-1 rounded-[10px] text-[12px] font-medium">{t("admin.adjust_balance")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingUser && (
        <AdjustBalanceModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function PTORulesTab() {
  const { t } = useI18n();
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null); // null|"new"|rule object
  const empty = {
    location: "",
    contract_type: "",
    payroll_company: "",
    pto_annual_days: 10,
    vacation_annual_days: 15,
    pto_carryover_max: 0,
    vacation_carryover_max: 5,
    accrual_method: "annual",
    probation_months: 0,
    pto_expiration_months: 12,
    vacation_expiration_months: 24,
    description: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pto-rules");
    setRules(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm(empty);
    setEditing("new");
  };
  const startEdit = (r) => {
    setForm({ ...empty, ...r, contract_type: r.contract_type || "", payroll_company: r.payroll_company || "" });
    setEditing(r);
  };
  const cancel = () => {
    setEditing(null);
    setForm(empty);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        contract_type: form.contract_type || null,
        payroll_company: form.payroll_company?.trim() || null,
        pto_annual_days: parseFloat(form.pto_annual_days),
        vacation_annual_days: parseFloat(form.vacation_annual_days),
        pto_carryover_max: parseFloat(form.pto_carryover_max),
        vacation_carryover_max: parseFloat(form.vacation_carryover_max),
        probation_months: parseInt(form.probation_months || 0),
        pto_expiration_months: parseInt(form.pto_expiration_months || 12),
        vacation_expiration_months: parseInt(form.vacation_expiration_months || 24),
      };
      if (editing === "new") {
        await api.post("/pto-rules", payload);
      } else {
        await api.patch(`/pto-rules/${editing.id}`, payload);
      }
      await load();
      cancel();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin.rule_delete_confirm"))) return;
    await api.delete(`/pto-rules/${id}`);
    await load();
  };

  const handleExport = () => {
    if (!rules.length) { alert(t("common.nothing_export")); return; }
    const cols = [
      "location", "payroll_company", "contract_type",
      "pto_annual_days", "vacation_annual_days",
      "pto_carryover_max", "vacation_carryover_max",
      "accrual_method", "probation_months",
      "pto_expiration_months", "vacation_expiration_months",
      "description",
    ];
    const lines = [cols.join(",")];
    rules.forEach((r) => {
      const row = cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",");
      lines.push(row);
    });
    const date = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `rules_by_location_${date}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end gap-2">
        <button data-testid="rules-export-csv" onClick={handleExport} className="sw-btn-secondary"><Download size={14} /> {t("common.export_csv")}</button>
        <button data-testid="new-rule-btn" onClick={startNew} className="sw-btn-primary">{t("admin.new_rule")}</button>
      </div>

      {editing && (
        <form onSubmit={save} className="sw-card grid grid-cols-1 md:grid-cols-2 gap-4 border-2 border-[#e2e8f0]">
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">
              {editing === "new" ? t("admin.new_rule") : t("admin.edit_rule")}
            </div>
            <button type="button" onClick={cancel} className="text-[#747474] hover:text-[#323232]"><X size={18} /></button>
          </div>
          <div>
            <label className="sw-label">{t("common.location")} *</label>
            <input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="sw-input" placeholder="USA - FL" data-testid="rule-location" />
          </div>
          <div>
            <label className="sw-label">{t("admin.payroll_company")}</label>
            <input value={form.payroll_company} onChange={(e) => setForm({ ...form, payroll_company: e.target.value })} className="sw-input" placeholder={t("admin.payroll_company_ph")} data-testid="rule-payroll-company" />
            <p className="text-[11px] text-[#94a3b8] mt-1">{t("admin.payroll_company_hint")}</p>
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.annual_days")}</label>
            <input type="number" step="0.5" value={form.pto_annual_days} onChange={(e) => setForm({ ...form, pto_annual_days: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.vacation_annual_days")}</label>
            <input type="number" step="0.5" value={form.vacation_annual_days} onChange={(e) => setForm({ ...form, vacation_annual_days: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto_carry_max")}</label>
            <input type="number" step="0.5" value={form.pto_carryover_max} onChange={(e) => setForm({ ...form, pto_carryover_max: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.vac_carry_max")}</label>
            <input type="number" step="0.5" value={form.vacation_carryover_max} onChange={(e) => setForm({ ...form, vacation_carryover_max: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.accrual_method")}</label>
            <select value={form.accrual_method} onChange={(e) => setForm({ ...form, accrual_method: e.target.value })} className="sw-input">
              <option value="annual">{t("admin.pto.annual_lump")}</option>
              <option value="monthly">{t("admin.pto.monthly_accrual")}</option>
            </select>
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.probation_months")}</label>
            <input type="number" min="0" value={form.probation_months} onChange={(e) => setForm({ ...form, probation_months: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.pto_expiration_months")}</label>
            <input type="number" min="1" value={form.pto_expiration_months} onChange={(e) => setForm({ ...form, pto_expiration_months: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto.vac_expiration_months")}</label>
            <input type="number" min="1" value={form.vacation_expiration_months} onChange={(e) => setForm({ ...form, vacation_expiration_months: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">{t("common.description")}</label>
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-rule-btn" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : t("common.save")}</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rules.map((r) => (
          <div key={r.id} className="sw-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#475569]">{r.location}</div>
                <div className="font-medium text-[16px] text-[#323232] mt-1">
                  {r.payroll_company || t("admin.all_contracts")}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(r)} className="sw-btn-secondary !px-3 !py-1.5 !text-[12px]">{t("common.edit")}</button>
                <button onClick={() => remove(r.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1.5 rounded-[10px] text-[12px] font-medium">{t("common.delete")}</button>
              </div>
            </div>
            {r.description && <p className="text-[12px] text-[#747474] mb-3">{r.description}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.pto_annual_label")}</div>
                <div className="text-[15px] font-medium">{r.pto_annual_days} d</div>
              </div>
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.vac_annual_label")}</div>
                <div className="text-[15px] font-medium">{r.vacation_annual_days} d</div>
              </div>
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.pto_carryover")}</div>
                <div className="text-[15px] font-medium">{r.pto_carryover_max} d</div>
              </div>
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.vac_carryover")}</div>
                <div className="text-[15px] font-medium">{r.vacation_carryover_max} d</div>
              </div>
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.accrual_method")}</div>
                <div className="text-[15px] font-medium capitalize">{r.accrual_method}</div>
              </div>
              <div className="bg-[#f6f6f6] rounded-[10px] px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-[#747474]">{t("admin.pto.probation_months")}</div>
                <div className="text-[15px] font-medium">{r.probation_months} mo</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CostCodesTab() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { code: "", description: "", active: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/cost-codes");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (r) => { setForm({ ...empty, ...r }); setEditing(r); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/cost-codes", form);
      else await api.patch(`/cost-codes/${editing.id}`, form);
      await load();
      cancel();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("admin.cc_delete_confirm"))) return;
    await api.delete(`/cost-codes/${id}`);
    await load();
  };

  const filtered = rows.filter((r) => !q || `${r.code} ${r.description}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row justify-between gap-3">
        <div className="relative flex-1 max-w-[420px]">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="sw-input pl-11" placeholder={t("admin.costcode.search")} />
        </div>
        <button data-testid="new-cc-btn" onClick={startNew} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_cost_code")}</button>
      </div>

      {editing && (
        <form onSubmit={save} className="sw-card grid grid-cols-1 md:grid-cols-3 gap-4 border-2 border-[#e2e8f0]">
          <div className="md:col-span-3 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{editing === "new" ? t("admin.new_cost_code") : t("admin.edit_cost_code")}</div>
            <button type="button" onClick={cancel} className="text-[#747474]"><X size={18} /></button>
          </div>
          <div>
            <label className="sw-label">Code *</label>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="sw-input" placeholder="CC-XXXX" />
          </div>
          <div className="md:col-span-2">
            <label className="sw-label">Description *</label>
            <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px] text-[#323232]">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />
            Active
          </label>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
          </div>
        </form>
      )}

      <div className="sw-card !p-0 overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Code</th>
              <th className="text-left px-5 py-3">Description</th>
              <th className="text-left px-5 py-3">{t("common.status")}</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[#eeeded] hover:bg-[#f6f6f6]">
                <td className="px-5 py-3 font-medium text-[#475569]">{r.code}</td>
                <td className="px-5 py-3">{r.description}</td>
                <td className="px-5 py-3">
                  {r.active ? <span className="sw-badge sw-badge-approved">{t("common.active")}</span> : <span className="sw-badge sw-badge-neutral">{t("common.inactive")}</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => startEdit(r)} className="text-[#475569] hover:bg-[#e2e8f0] px-3 py-1 rounded-[8px] text-[12px] font-medium mr-1">{t("common.edit")}</button>
                  <button onClick={() => remove(r.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1 rounded-[8px] text-[12px] font-medium">{t("common.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SuppliersTab() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", type: "both", tax_id: "", contact: "", active: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (r) => { setForm({ ...empty, ...r }); setEditing(r); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/suppliers", form);
      else await api.patch(`/suppliers/${editing.id}`, form);
      await load();
      cancel();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("admin.sup_delete_confirm"))) return;
    await api.delete(`/suppliers/${id}`);
    await load();
  };

  const filtered = rows.filter((r) => {
    if (filterType !== "all" && r.type !== filterType) return false;
    if (q && !`${r.name} ${r.contact || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const typeBadge = (type) => ({
    material: <span className="sw-badge sw-badge-gold">Material</span>,
    service: <span className="sw-badge sw-badge-neutral">Service</span>,
    both: <span className="sw-badge sw-badge-approved">Both</span>,
  })[type] || null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row justify-between gap-3">
        <div className="flex gap-3 flex-1 max-w-[600px]">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} className="sw-input pl-11" placeholder={t("admin.supplier.search")} />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="sw-input w-[160px]">
            <option value="all">{t("admin.supplier.all_types")}</option>
            <option value="material">Material</option>
            <option value="service">Service</option>
            <option value="both">Both</option>
          </select>
        </div>
        <button data-testid="new-supplier-btn" onClick={startNew} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_supplier")}</button>
      </div>

      {editing && (
        <form onSubmit={save} className="sw-card grid grid-cols-1 md:grid-cols-2 gap-4 border-2 border-[#e2e8f0]">
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{editing === "new" ? t("admin.new_supplier") : t("admin.edit_supplier")}</div>
            <button type="button" onClick={cancel} className="text-[#747474]"><X size={18} /></button>
          </div>
          <div>
            <label className="sw-label">Name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.type")}</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="sw-input">
              <option value="material">Material</option>
              <option value="service">Service</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div>
            <label className="sw-label">Tax ID / EIN</label>
            <input value={form.tax_id || ""} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Contact</label>
            <input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="sw-input" placeholder={t("admin.sup_contact_ph")} />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px] text-[#323232]">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />
            Active
          </label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
          </div>
        </form>
      )}

      <div className="sw-card !p-0 overflow-x-auto">
        <table className="w-full text-[14px] min-w-[700px]">
          <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">{t("common.name")}</th>
              <th className="text-left px-5 py-3">{t("common.type")}</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">{t("common.status")}</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-[#eeeded] hover:bg-[#f6f6f6]">
                <td className="px-5 py-3 font-medium">{r.name}</td>
                <td className="px-5 py-3">{typeBadge(r.type)}</td>
                <td className="px-5 py-3 text-[#747474] text-[13px]">{r.contact || "—"}</td>
                <td className="px-5 py-3">
                  {r.active ? <span className="sw-badge sw-badge-approved">{t("common.active")}</span> : <span className="sw-badge sw-badge-neutral">{t("common.inactive")}</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => startEdit(r)} className="text-[#475569] hover:bg-[#e2e8f0] px-3 py-1 rounded-[8px] text-[12px] font-medium mr-1">{t("common.edit")}</button>
                  <button onClick={() => remove(r.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1 rounded-[8px] text-[12px] font-medium">{t("common.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab() {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [editing, setEditing] = useState(null); // user object → opens full EditUserModal
  const [editingPayment, setEditingPayment] = useState(null); // opens PaymentEditorModal (legacy narrow flow)
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [filterStatus, setFilterStatus] = useState("active");
  const [view, setView] = useState("list"); // "list" | "tree"
  const [collapsed, setCollapsed] = useState({}); // { managerId: true } → hide direct reports
  const [syncingUser, setSyncingUser] = useState({});

  const syncEmployeeQbo = async (uid) => {
    setSyncingUser((s) => ({ ...s, [uid]: true }));
    try {
      const { data } = await api.post(`/qbo/sync-team member/${uid}`);
      setUsers((prev) => prev.map((x) => x.id === uid ? { ...x, qbo_vendor_id: data.qbo_vendor_id || x.qbo_vendor_id } : x));
      toast.success(t("qbo.synced_ok"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "QBO sync failed");
    } finally {
      setSyncingUser((s) => ({ ...s, [uid]: false }));
    }
  };

  const load = async () => {
    const [{ data: us }, { data: ds }] = await Promise.all([
      api.get("/users"),
      api.get("/departments"),
    ]);
    setUsers(us);
    setDepts(ds || []);
  };
  useEffect(() => { load(); }, []);

  const deptById = useMemo(() => Object.fromEntries((depts || []).map((d) => [d.id, d])), [depts]);
  const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (filterDept !== "all" && u.department_id !== filterDept) return false;
      if (filterStatus === "active" && u.active === false) return false;
      if (filterStatus === "inactive" && u.active !== false) return false;
      if (!needle) return true;
      const hay = [u.name, u.email, u.job_title, u.location, u.cost_code].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q, filterRole, filterDept, filterStatus]);

  // ── Tree view ────────────────────────────────────────────────
  // Build a map { managerId | 'ROOT' → [users…] } from the filtered list.
  // When a search/filter is active we still want to show ancestor chains so
  // the user has context ("who does this person report to"), so we auto-expand
  // ancestors of every match.
  const { rootIds, childrenByMgr, spanOfControl, matchedSet } = useMemo(() => {
    const matched = new Set(filtered.map((u) => u.id));
    const expandedAncestors = new Set(matched);
    // Walk each match up to root, collecting ancestor ids so we can render the chain.
    filtered.forEach((u) => {
      let cur = userById[u.manager_id];
      while (cur && !expandedAncestors.has(cur.id)) {
        expandedAncestors.add(cur.id);
        cur = userById[cur.manager_id];
      }
    });
    const visible = (users || []).filter((u) => expandedAncestors.has(u.id));
    const byMgr = {};
    const roots = [];
    visible.forEach((u) => {
      const mgrId = u.manager_id && u.manager_id !== u.id && userById[u.manager_id] ? u.manager_id : "ROOT";
      if (mgrId === "ROOT") roots.push(u.id);
      byMgr[mgrId] = byMgr[mgrId] || [];
      byMgr[mgrId].push(u);
    });
    // Sort each bucket alphabetically for stability
    Object.values(byMgr).forEach((arr) => arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    roots.sort((a, b) => (userById[a]?.name || "").localeCompare(userById[b]?.name || ""));
    // Compute full span-of-control (recursive count) per user for badges.
    const span = {};
    const computeSpan = (id) => {
      if (span[id] != null) return span[id];
      const direct = byMgr[id] || [];
      let total = direct.length;
      direct.forEach((c) => { total += computeSpan(c.id); });
      span[id] = total;
      return total;
    };
    Object.keys(byMgr).forEach((mid) => { if (mid !== "ROOT") computeSpan(mid); });
    return { rootIds: roots, childrenByMgr: byMgr, spanOfControl: span, matchedSet: matched };
  }, [filtered, users, userById]);

  const renderTreeNode = (u, depth) => {
    const kids = childrenByMgr[u.id] || [];
    const hasKids = kids.length > 0;
    const isCollapsed = collapsed[u.id];
    const dept = deptById[u.department_id];
    const isMatch = matchedSet.has(u.id);
    return (
      <div key={u.id} data-testid={`tree-node-${u.id}`}>
        <div
          className={`flex items-center gap-3 py-2.5 pr-3 rounded-[10px] transition-colors ${isMatch ? "hover:bg-[#f6f6f6]" : "opacity-60 hover:opacity-80"}`}
          style={{ paddingLeft: depth * 24 }}
        >
          <button
            type="button"
            onClick={() => hasKids && setCollapsed({ ...collapsed, [u.id]: !isCollapsed })}
            className={`w-5 h-5 flex items-center justify-center rounded-[6px] ${hasKids ? "text-[#475569] hover:bg-[#e2e8f0]" : "text-transparent"}`}
            data-testid={`tree-toggle-${u.id}`}
          >
            {hasKids ? (isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />) : <span className="w-1.5 h-1.5 rounded-full bg-[#cbd5e1]" />}
          </button>
          <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <div className="text-[13.5px] font-medium text-[#0f172a] truncate">{u.name}</div>
            <span className="px-2 py-0.5 rounded-full bg-[#eef2f7] text-[10.5px] capitalize">{u.role}</span>
            {u.job_title && <span className="text-[12px] text-[#475569] truncate">{u.job_title}</span>}
            {dept && <span className="text-[11px] text-[#94a3b8]">· {dept.name}</span>}
            {hasKids && (
              <span className="text-[10.5px] px-1.5 py-0.5 rounded-full bg-[#e0e7ff] text-[#4338ca] font-medium" data-testid={`span-${u.id}`}>
                {kids.length} {t("admin.direct_reports")}
                {spanOfControl[u.id] > kids.length && ` · ${spanOfControl[u.id]} ${t("admin.total_reports")}`}
              </span>
            )}
            {u.active === false && <span className="sw-badge sw-badge-rejected !text-[10px]">{t("admin.employees_inactive")}</span>}
          </div>
          <button
            data-testid={`edit-user-${u.id}`}
            onClick={() => setEditing(u)}
            className="text-[#475569] hover:bg-[#e2e8f0] px-2.5 py-1 rounded-[8px] text-[11.5px] font-medium inline-flex items-center gap-1 whitespace-nowrap"
          >
            <Pencil size={11} /> {t("admin.actions_edit")}
          </button>
        </div>
        {hasKids && !isCollapsed && (
          <div>{kids.map((c) => renderTreeNode(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header: search + filters + view toggle + New */}
      <div className="sw-card !p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input
            data-testid="users-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.search_users_ph")}
            className="sw-input !pl-9"
          />
        </div>
        <select data-testid="users-filter-role" value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="sw-input !w-auto">
          <option value="all">{t("admin.all_roles")}</option>
          <option value="employee">{t("common.employee")}</option>
          <option value="manager">{t("common.manager")}</option>
          <option value="hr">HR</option>
          <option value="ceo">CEO</option>
          <option value="admin">{t("common.admin")}</option>
        </select>
        <select data-testid="users-filter-dept" value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="sw-input !w-auto max-w-[220px]">
          <option value="all">{t("admin.all_depts")}</option>
          {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select data-testid="users-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="sw-input !w-auto">
          <option value="active">{t("admin.filter_active")}</option>
          <option value="inactive">{t("admin.filter_inactive")}</option>
          <option value="all">{t("common.all")}</option>
        </select>
        {/* View toggle */}
        <div className="inline-flex bg-[#f1f5f9] rounded-[10px] p-0.5" data-testid="users-view-toggle">
          <button
            type="button"
            data-testid="users-view-list"
            onClick={() => setView("list")}
            className={`px-3 py-1.5 text-[12px] font-medium rounded-[8px] transition-all ${view === "list" ? "bg-white shadow-sm text-[#0f172a]" : "text-[#64748b] hover:text-[#0f172a]"}`}
          >{t("admin.list_view")}</button>
          <button
            type="button"
            data-testid="users-view-tree"
            onClick={() => setView("tree")}
            className={`px-3 py-1.5 text-[12px] font-medium rounded-[8px] transition-all ${view === "tree" ? "bg-white shadow-sm text-[#0f172a]" : "text-[#64748b] hover:text-[#0f172a]"}`}
          >{t("admin.by_manager")}</button>
        </div>
        <button data-testid="new-user-btn" onClick={() => setCreating(true)} className="sw-btn-primary">
          <Plus size={14} /> {t("admin.new_collab")}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[#747474]" data-testid="users-count">
          {t("admin.of_x_collabs", { filtered: filtered.length, total: users.length })}
        </div>
        {view === "tree" && (
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="tree-expand-all"
              onClick={() => setCollapsed({})}
              className="text-[11.5px] text-[#475569] hover:underline"
            >{t("admin.expand_all")}</button>
            <span className="text-[#cbd5e1]">·</span>
            <button
              type="button"
              data-testid="tree-collapse-all"
              onClick={() => {
                const next = {};
                users.forEach((u) => { if ((childrenByMgr[u.id] || []).length) next[u.id] = true; });
                setCollapsed(next);
              }}
              className="text-[11.5px] text-[#475569] hover:underline"
            >{t("admin.collapse_all")}</button>
          </div>
        )}
      </div>

      {view === "list" && (
        <div className="sw-card !p-0 overflow-x-auto">
          <table className="w-full text-[13.5px] min-w-[1000px]">
            <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">{t("admin.employees_name")}</th>
                <th className="text-left px-5 py-3">{t("common.email")}</th>
                <th className="text-left px-5 py-3">{t("common.role")}</th>
                <th className="text-left px-5 py-3">{t("common.department")}</th>
                <th className="text-left px-5 py-3">{t("common.manager")}</th>
                <th className="text-left px-5 py-3">{t("common.job_title")}</th>
                <th className="text-left px-5 py-3">QBO</th>
                <th className="text-left px-5 py-3">{t("common.status")}</th>
                <th className="text-right px-5 py-3">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const dept = deptById[u.department_id];
                const mgr = userById[u.manager_id];
                return (
                  <tr key={u.id} className={`border-t border-[#eeeded] hover:bg-[#f6f6f6] ${u.active === false ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3 font-medium">{u.name}</td>
                    <td className="px-5 py-3 text-[#475569]">{u.email}</td>
                    <td className="px-5 py-3 capitalize"><span className="px-2 py-0.5 rounded-full bg-[#eef2f7] text-[11.5px]">{u.role}</span></td>
                    <td className="px-5 py-3 text-[#475569]">{dept?.name || "—"}</td>
                    <td className="px-5 py-3 text-[#475569]">{mgr?.name || "—"}</td>
                    <td className="px-5 py-3 text-[#475569]">{u.job_title || "—"}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1.5">
                        {u.qbo_vendor_id ? (
                          <code className="text-[10.5px] bg-[#dcfce7] text-[#166534] px-1.5 py-0.5 rounded" title={u.qbo_vendor_id}>
                            #{String(u.qbo_vendor_id).slice(-6)}
                          </code>
                        ) : (
                          <span className="text-[10.5px] text-[#94a3b8]">—</span>
                        )}
                        <button
                          type="button"
                          onClick={() => syncEmployeeQbo(u.id)}
                          disabled={syncingUser[u.id]}
                          className="text-[#475569] hover:bg-[#e2e8f0] p-1 rounded-[6px] disabled:opacity-40"
                          title={u.qbo_vendor_id ? "Re-sync QBO" : "Force sync QBO"}
                          data-testid={`user-qbo-sync-${u.id}`}
                        >
                          <RefreshCw size={11} className={syncingUser[u.id] ? "animate-spin" : ""} />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      {u.active === false
                        ? <span className="sw-badge sw-badge-rejected">{t("admin.employees_inactive")}</span>
                        : <span className="sw-badge sw-badge-approved">{t("admin.employees_active")}</span>}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)} className="text-[#475569] hover:bg-[#e2e8f0] px-2.5 py-1 rounded-[8px] text-[12px] font-medium inline-flex items-center gap-1">
                        <Pencil size={12} /> {t("admin.actions_edit")}
                      </button>
                      {u.contract_type === "PJ_Lummar" || u.contract_type === "PJ_Direct" ? (
                        <button data-testid={`edit-payment-${u.id}`} onClick={() => setEditingPayment(u)} className="text-[#475569] hover:bg-[#e2e8f0] px-2.5 py-1 rounded-[8px] text-[12px] font-medium ml-1">
                          Payment
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-10 text-[#94a3b8]">{t("admin.no_users_filters")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === "tree" && (
        <div className="sw-card !p-3" data-testid="users-tree">
          {rootIds.length === 0 ? (
            <div className="text-center py-10 text-[#94a3b8] text-[13px]">{t("admin.no_users_filters")}</div>
          ) : (
            rootIds.map((rid) => renderTreeNode(userById[rid], 0))
          )}
        </div>
      )}

      {editing && (
        <EditUserModal
          user={editing}
          users={users}
          departments={depts}
          isAdmin={isAdmin}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {editingPayment && (
        <PaymentEditorModal user={editingPayment} onClose={() => setEditingPayment(null)} onSaved={() => { setEditingPayment(null); load(); }} />
      )}
      {creating && (
        <NewUserModal users={users} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function EditUserModal({ user, users, departments, isAdmin, onClose, onSaved }) {
  const { t } = useI18n();
  const [payrollCompanies, setPayrollCompanies] = useState([]);
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "employee",
    phone: user.phone || "",
    job_title: user.job_title || "",
    department_id: user.department_id || "",
    manager_id: user.manager_id || "",
    location: user.location || "",
    cost_code: user.cost_code || "",
    contract_type: user.contract_type || "",
    payroll_company: user.payroll_company || "",
    frankcrum_id: user.frankcrum_id || "",
    personal_email: user.personal_email || "",
    emergency_contact: user.emergency_contact || "",
    birth_date: user.birth_date || "",
    hire_date: user.hire_date || "",
    pto_balance: user.pto_balance ?? 0,
    vacation_balance: user.vacation_balance ?? 0,
    active: user.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const [showPwdSection, setShowPwdSection] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    api.get("/pto-rules").then(({ data }) => {
      const pcs = Array.from(new Set((data || []).map((r) => r.payroll_company).filter(Boolean))).sort();
      setPayrollCompanies(pcs);
    }).catch(() => setPayrollCompanies([]));
  }, []);

  // Only send fields that changed — keeps the PATCH body minimal and avoids
  // hitting the backend's per-role allowlist with untouched values that would
  // otherwise reject the request for HR editing an admin field.
  const buildPayload = () => {
    const payload = {};
    const src = user;
    const numFields = new Set(["pto_balance", "vacation_balance"]);
    Object.entries(form).forEach(([k, v]) => {
      let a = src[k];
      let b = v;
      if (numFields.has(k)) { a = Number(a ?? 0); b = Number(b ?? 0); }
      if ((a || "") === (b || "") && a !== b) return; // skip strict equal
      if (a === b) return;
      if (b === "" && (a === null || a === undefined)) return;
      payload[k] = b === "" ? null : b;
    });
    // HR is not allowed to touch role/email — strip if present.
    if (!isAdmin) {
      delete payload.role;
      delete payload.email;
    }
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (Object.keys(payload).length === 0) {
        toast.info(t("admin.no_changes"));
        setSaving(false);
        return;
      }
      await api.patch(`/users/${user.id}`, payload);
      toast.success(t("admin.user_updated"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.save_fail"));
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error(t("admin.pwd_min"));
      return;
    }
    setPwdBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { password: newPassword });
      toast.success(t("admin.pwd_reset_done"));
      setNewPassword("");
      setShowPwdSection(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.reset_pwd_fail"));
    } finally {
      setPwdBusy(false);
    }
  };

  const toggleActive = async () => {
    const nextActive = !form.active;
    if (!nextActive && !confirm(t("admin.deactivate_confirm", { name: user.name }))) return;
    try {
      await api.patch(`/users/${user.id}`, { active: nextActive });
      toast.success(nextActive ? t("admin.reactivated") : t("admin.deactivated"));
      setForm({ ...form, active: nextActive });
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("admin.status_change_fail"));
    }
  };

  const managerOptions = (users || []).filter((u) => u.id !== user.id && ["manager", "ceo", "hr", "admin"].includes(u.role));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[720px] w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-[#f1f5f9]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.edit_employee")}</div>
            <h3 className="sw-heading text-[20px] mt-0.5">{user.name}</h3>
            <div className="text-[12px] text-[#94a3b8]">{user.email}</div>
          </div>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-[#475569]" data-testid="close-edit-user"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4">
          <div className="col-span-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("admin.registration_data")}</div>

          <div>
            <label className="sw-label">{t("common.name")}</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="edit-user-name" />
          </div>
          <div>
            <label className="sw-label">{t("common.job_title")}</label>
            <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="sw-input" data-testid="edit-user-job-title" />
          </div>

          <div>
            <label className="sw-label">{t("common.email")} {isAdmin ? "" : t("admin.only_admin")}</label>
            <input type="email" disabled={!isAdmin} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sw-input disabled:bg-[#f6f6f6]" data-testid="edit-user-email" />
          </div>
          <div>
            <label className="sw-label">{t("common.role")} {isAdmin ? "" : t("admin.only_admin")}</label>
            <select disabled={!isAdmin} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="sw-input disabled:bg-[#f6f6f6]" data-testid="edit-user-role">
              <option value="employee">{t("common.employee")}</option>
              <option value="manager">{t("common.manager")}</option>
              <option value="hr">HR</option>
              <option value="ceo">CEO</option>
              <option value="admin">{t("common.admin")}</option>
            </select>
          </div>

          <div>
            <label className="sw-label">{t("common.department")}</label>
            <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="sw-input" data-testid="edit-user-department">
              <option value="">—</option>
              {(departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sw-label">{t("common.manager")}</label>
            <select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} className="sw-input" data-testid="edit-user-manager">
              <option value="">—</option>
              {managerOptions.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>

          <div>
            <label className="sw-label">{t("admin.payroll_company")}</label>
            <select value={form.payroll_company} onChange={(e) => setForm({ ...form, payroll_company: e.target.value })} className="sw-input" data-testid="edit-user-payroll-company">
              <option value="">—</option>
              {payrollCompanies.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
            </select>
            {payrollCompanies.length === 0 && (
              <p className="text-[11px] text-[#94a3b8] mt-1">{t("admin.no_payroll_companies")}</p>
            )}
          </div>
          <div>
            <label className="sw-label">Cost code</label>
            <input value={form.cost_code} onChange={(e) => setForm({ ...form, cost_code: e.target.value })} className="sw-input" />
          </div>

          <div>
            <label className="sw-label">{t("common.location")}</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">FrankCrum ID</label>
            <input value={form.frankcrum_id} onChange={(e) => setForm({ ...form, frankcrum_id: e.target.value })} className="sw-input" />
          </div>

          <div className="col-span-2 mt-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("admin.contact_section")}</div>
          <div>
            <label className="sw-label">{t("common.phone")}</label>
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.personal_email")}</label>
            <input type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} className="sw-input" />
          </div>
          <div className="col-span-2">
            <label className="sw-label">{t("common.emergency_contact")}</label>
            <input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className="sw-input" placeholder={t("admin.emergency_contact_ph")} />
          </div>

          <div className="col-span-2 mt-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("admin.dates_balances")}</div>
          <div>
            <label className="sw-label">{t("common.birth_date")}</label>
            <input type="date" value={form.birth_date ? form.birth_date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.hire_date")}</label>
            <input type="date" value={form.hire_date ? form.hire_date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto_balance_days")}</label>
            <input type="number" step="0.5" value={form.pto_balance} onChange={(e) => setForm({ ...form, pto_balance: parseFloat(e.target.value) || 0 })} className="sw-input" data-testid="edit-user-pto" />
          </div>
          <div>
            <label className="sw-label">{t("admin.vac_balance_days")}</label>
            <input type="number" step="0.5" value={form.vacation_balance} onChange={(e) => setForm({ ...form, vacation_balance: parseFloat(e.target.value) || 0 })} className="sw-input" data-testid="edit-user-vacation" />
          </div>

          {/* QBO Sync */}
          <div className="col-span-2 mt-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">QBO Sync</div>
          <div className="col-span-2 flex items-center justify-between bg-[#f8fafc] border border-[#e2e8f0] rounded-[12px] px-4 py-3" data-testid="user-qbo-block">
            <div>
              <div className="text-[12px] font-medium text-[#0f172a]">QBO Vendor ID</div>
              {user.qbo_vendor_id ? (
                <div className="mt-0.5 flex items-center gap-2">
                  <code className="text-[11.5px] bg-[#dcfce7] text-[#166534] px-2 py-0.5 rounded" data-testid="user-qbo-id">{user.qbo_vendor_id}</code>
                  <span className="text-[11px] text-[#16a34a]">✓ {t("qbo.conn_connected")}</span>
                </div>
              ) : (
                <div className="text-[12px] text-[#94a3b8] mt-0.5">{t("qbo.not_synced")}</div>
              )}
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.post(`/qbo/sync-team member/${user.id}`);
                  toast.success(t("qbo.synced_ok") || "QBO sync OK");
                  onSaved();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "QBO sync failed");
                }
              }}
              data-testid="user-qbo-sync"
              className="sw-btn-secondary !text-[12px]"
            >
              <RefreshCw size={12} /> {user.qbo_vendor_id ? "Re-sync" : t("qbo.sync")}
            </button>
          </div>

          {/* Danger zone: status + password */}
          <div className="col-span-2 mt-4 border-t border-[#f1f5f9] pt-4 flex flex-col gap-3">
            <div className="flex items-center justify-between bg-[#fef8f2] border border-[#f5e8d5] rounded-[12px] px-4 py-3">
              <div>
                <div className="text-[13px] font-semibold text-[#7c5a1e]">{t("admin.account_status")}</div>
                <div className="text-[12px] text-[#94a3b8]">{form.active ? t("admin.active_login") : t("admin.deactivated_login")}</div>
              </div>
              <button type="button" onClick={toggleActive} data-testid="toggle-active" className={`sw-btn-secondary ${form.active ? "!text-[#881a27]" : "!text-[#0f766e]"}`}>
                {form.active ? <><UserX size={13} /> {t("admin.deactivate")}</> : <><UserCheck size={13} /> {t("admin.reactivate")}</>}
              </button>
            </div>

            {isAdmin && (
              <div className="bg-[#fef8f2] border border-[#f5e8d5] rounded-[12px] px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[13px] font-semibold text-[#7c5a1e]">{t("admin.reset_password")}</div>
                    <div className="text-[12px] text-[#94a3b8]">{t("admin.reset_pwd_hint")}</div>
                  </div>
                  <button type="button" onClick={() => setShowPwdSection(!showPwdSection)} data-testid="toggle-pwd-section" className="sw-btn-secondary">
                    <RotateCcw size={13} /> {showPwdSection ? t("admin.close_pwd") : t("admin.change_pwd")}
                  </button>
                </div>
                {showPwdSection && (
                  <div className="mt-3 flex items-end gap-2">
                    <div className="flex-1">
                      <label className="sw-label">{t("admin.new_pwd_ph")}</label>
                      <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="sw-input" data-testid="edit-user-new-password" placeholder="Sagewood@2026" />
                    </div>
                    <button type="button" onClick={resetPassword} disabled={pwdBusy} className="sw-btn-primary" data-testid="save-new-password">
                      {pwdBusy ? t("admin.saving_pwd") : t("admin.save_pwd")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="col-span-2 flex justify-end gap-2 pt-4 border-t border-[#f1f5f9]">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="sw-btn-primary" data-testid="save-edit-user-btn">
              <Save size={13} /> {saving ? t("admin.saving_pwd") : t("admin.save_changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewUserModal({ users, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: "", email: "", password: "Sagewood@2026",
    role: "employee", payroll_company: "", location: "Tampa",
    job_title: "", manager_id: "", department_id: "",
  });
  const [depts, setDepts] = useState([]);
  const [payrollCompanies, setPayrollCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data || []));
    api.get("/pto-rules").then(({ data }) => {
      const pcs = Array.from(new Set((data || []).map((r) => r.payroll_company).filter(Boolean))).sort();
      setPayrollCompanies(pcs);
    }).catch(() => setPayrollCompanies([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        payroll_company: form.payroll_company || null,
        manager_id: form.manager_id || null,
        department_id: form.department_id || null,
      };
      await api.post("/auth/register", payload);
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error creating user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-user-modal">
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[20px]">{t("admin.new_employee")}</h3>
          <button onClick={onClose} className="text-[#94a3b8]" data-testid="close-new-user-modal"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="sw-label">{t("admin.field_name")} *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="new-user-name" />
          </div>
          <div className="col-span-2">
            <label className="sw-label">{t("common.email")} *</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="sw-input" data-testid="new-user-email" />
          </div>
          <div>
            <label className="sw-label">{t("admin.field_password")} *</label>
            <input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="sw-input" data-testid="new-user-password" />
          </div>
          <div>
            <label className="sw-label">{t("common.role")}</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="sw-input" data-testid="new-user-role">
              <option value="employee">{t("common.employee")}</option>
              <option value="manager">{t("common.manager")}</option>
              <option value="hr">HR</option>
              <option value="ceo">CEO</option>
              <option value="admin">{t("common.admin")}</option>
            </select>
          </div>
          <div>
            <label className="sw-label">{t("admin.payroll_company")}</label>
            <select value={form.payroll_company} onChange={(e) => setForm({ ...form, payroll_company: e.target.value })} className="sw-input" data-testid="new-user-payroll-company">
              <option value="">—</option>
              {payrollCompanies.map((pc) => <option key={pc} value={pc}>{pc}</option>)}
            </select>
            {payrollCompanies.length === 0 && (
              <p className="text-[11px] text-[#94a3b8] mt-1">{t("admin.no_payroll_companies")}</p>
            )}
          </div>
          <div>
            <label className="sw-label">{t("common.location")}</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="sw-input" />
          </div>
          <div className="col-span-2">
            <label className="sw-label">{t("common.job_title")}</label>
            <input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.department")}</label>
            <select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="sw-input">
              <option value="">—</option>
              {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sw-label">{t("common.manager")}</label>
            <select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} className="sw-input" data-testid="new-user-manager">
              <option value="">—</option>
              {users.filter((u) => ["manager", "ceo", "hr", "admin"].includes(u.role)).map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </select>
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-[#f1f5f9]">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="sw-btn-primary" data-testid="save-new-user-btn">
              {saving ? t("admin.creating") : t("admin.create_employee")}
            </button>
          </div>
        </form>
        <p className="text-[11px] text-[#94a3b8] mt-3">{t("admin.after_create_qbo")} <strong>{t("admin.qbo_hint_path")}</strong> {t("admin.qbo_hint_click")} <strong>{t("qbo.sync")}</strong> {t("admin.after_create_qbo2")}</p>
      </div>
    </div>
  );
}

// ============ Access Profiles (with cadastrable permission tree) ============
function AccessProfilesTab() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", description: "", permission_tree: [], permissions: [], active: true };
  const [form, setForm] = useState(empty);

  const load = async () => setRows((await api.get("/access-profiles")).data);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/access-profiles", form);
      else await api.patch(`/access-profiles/${editing.id}`, form);
      await load();
      setEditing(null);
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => { if (window.confirm(t("common.delete_q"))) { await api.delete(`/access-profiles/${id}`); await load(); } };

  const addSystem = () => {
    const name = window.prompt(t("admin.system_name") + ":");
    if (!name?.trim()) return;
    setForm({ ...form, permission_tree: [...(form.permission_tree || []), { system: name.trim(), modules: [] }] });
  };

  const applyStarterTemplate = () => {
    if ((form.permission_tree || []).length > 0 && !window.confirm(t("admin.access_starter_confirm"))) return;
    setForm({
      ...form,
      permission_tree: [
        { system: "People", modules: [
          { name: "Profile", folders: ["My profile", "Documents"] },
          { name: "PTO / Vacation", folders: ["My requests", "Team requests"] },
          { name: "Onboarding", folders: ["Tasks", "Checklist"] },
        ] },
        { system: "Finance", modules: [
          { name: "Reimbursements", folders: ["Submit", "My history"] },
          { name: "Travel", folders: ["Requests", "Reports"] },
          { name: "Payroll", folders: ["Payslips"] },
        ] },
        { system: "Operations", modules: [
          { name: "Projects", folders: ["Board", "Timesheet"] },
          { name: "Documents", folders: ["Contracts", "Manuals"] },
          { name: "Knowledge Base", folders: ["Public", "Team"] },
        ] },
      ],
    });
  };
  const removeSystem = (si) => setForm({ ...form, permission_tree: (form.permission_tree || []).filter((_, i) => i !== si) });
  const addModule = (si) => {
    const name = window.prompt(t("admin.module_name") + ":");
    if (!name?.trim()) return;
    const tree = [...(form.permission_tree || [])];
    tree[si] = { ...tree[si], modules: [...(tree[si].modules || []), { name: name.trim(), folders: [] }] };
    setForm({ ...form, permission_tree: tree });
  };
  const removeModule = (si, mi) => {
    const tree = [...(form.permission_tree || [])];
    tree[si] = { ...tree[si], modules: tree[si].modules.filter((_, i) => i !== mi) };
    setForm({ ...form, permission_tree: tree });
  };
  const addFolder = (si, mi) => {
    const name = window.prompt(t("admin.folder_name") + ":");
    if (!name?.trim()) return;
    const tree = [...(form.permission_tree || [])];
    const mods = [...tree[si].modules];
    mods[mi] = { ...mods[mi], folders: [...(mods[mi].folders || []), name.trim()] };
    tree[si] = { ...tree[si], modules: mods };
    setForm({ ...form, permission_tree: tree });
  };
  const removeFolder = (si, mi, fi) => {
    const tree = [...(form.permission_tree || [])];
    const mods = [...tree[si].modules];
    mods[mi] = { ...mods[mi], folders: mods[mi].folders.filter((_, i) => i !== fi) };
    tree[si] = { ...tree[si], modules: mods };
    setForm({ ...form, permission_tree: tree });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button data-testid="new-access-profile-btn" onClick={() => { setForm(empty); setEditing("new"); }} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_profile")}</button>
      </div>
      {editing && (
        <form onSubmit={save} className="sw-card grid grid-cols-1 gap-4 border-2 border-[#e2e8f0]">
          <div className="flex justify-between items-center">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{editing === "new" ? t("admin.new_profile") : t("common.edit")} · Access Profile</div>
            <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
          </div>
          <div><label className="sw-label">{t("common.name")} *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" placeholder="e.g. Field Worker" data-testid="access-profile-name" /></div>
          <div><label className="sw-label">{t("common.description")}</label><textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>

          {/* Permission tree */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <label className="sw-label !mb-0">{t("admin.access_profile_tree")}</label>
              <div className="flex gap-2">
                <button type="button" onClick={applyStarterTemplate} data-testid="starter-template-btn" className="sw-btn-secondary !text-[11.5px] !px-3 !py-1.5"><Sparkles size={12} /> {t("admin.access_starter_btn")}</button>
                <button type="button" onClick={addSystem} data-testid="add-system-btn" className="sw-btn-secondary !text-[12px] !px-3 !py-1.5"><Plus size={12} /> {t("admin.new_system")}</button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {(form.permission_tree || []).length === 0 && (
                <div className="text-[12px] text-[#94a3b8] italic p-3 bg-[#f8fafc] rounded-[10px]">{t("admin.access_profile_tree")}</div>
              )}
              {(form.permission_tree || []).map((sys, si) => (
                <div key={si} className="border border-[#e2e8f0] rounded-[10px] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-[13px] text-[#0f172a]">📁 {sys.system}</div>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => addModule(si)} className="text-[11px] text-[#475569] hover:bg-[#e2e8f0] px-2 py-1 rounded-[6px]"><Plus size={10} className="inline" /> {t("admin.new_module")}</button>
                      <button type="button" onClick={() => removeSystem(si)} className="text-[11px] text-[#881a27] hover:bg-[#e5c1c1] px-2 py-1 rounded-[6px]"><Trash2 size={10} className="inline" /></button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 ml-4">
                    {(sys.modules || []).map((mod, mi) => (
                      <div key={mi} className="border-l-2 border-[#cbd5e1] pl-3">
                        <div className="flex items-center justify-between">
                          <div className="text-[12px] text-[#475569]">▸ {mod.name}</div>
                          <div className="flex gap-1">
                            <button type="button" onClick={() => addFolder(si, mi)} className="text-[10px] text-[#475569] hover:bg-[#e2e8f0] px-2 py-0.5 rounded-[6px]"><Plus size={9} className="inline" /> {t("admin.new_folder")}</button>
                            <button type="button" onClick={() => removeModule(si, mi)} className="text-[10px] text-[#881a27] hover:bg-[#e5c1c1] px-2 py-0.5 rounded-[6px]"><X size={10} /></button>
                          </div>
                        </div>
                        {(mod.folders || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1 ml-4">
                            {mod.folders.map((f, fi) => (
                              <span key={fi} className="sw-badge sw-badge-neutral !text-[10px] inline-flex items-center gap-1">
                                {f}
                                <button type="button" onClick={() => removeFolder(si, mi, fi)} className="hover:bg-[#94a3b8] rounded-full p-0.5"><X size={8} /></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />{t("common.active")}</label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" className="sw-btn-primary" data-testid="save-access-profile">{t("common.save")}</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((r) => {
          const treeCount = (r.permission_tree || []).length;
          const modCount = (r.permission_tree || []).reduce((a, s) => a + (s.modules || []).length, 0);
          return (
            <div key={r.id} className="sw-card" data-testid={`access-profile-card-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#475569]">{r.active ? t("common.active") : t("common.inactive")}</div>
                  <div className="font-medium text-[16px] text-[#323232] mt-1">{r.name}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setForm({ ...empty, ...r, permission_tree: r.permission_tree || [] }); setEditing(r); }} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5">{t("common.edit")}</button>
                  <button onClick={() => remove(r.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1.5 rounded-[10px] text-[12px] font-medium">{t("common.delete")}</button>
                </div>
              </div>
              {r.description && <p className="text-[12px] text-[#747474] mb-3">{r.description}</p>}
              <div className="text-[11px] text-[#64748b]">
                {treeCount} sistemas · {modCount} módulos
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ Positions (formerly Job Profiles) — with Employee Profile + Job Profile sections ============

const BEHAVIORAL_DESCRIPTIONS = {
  "Comunicador": "Excelentes habilidades de comunicação, expressivo, carismático, transmite ideias com clareza e persuasão. Ideal para vendas, marketing e relações públicas.",
  "Planejador": "Habilidade de organização e planejamento estratégico. Visão de longo prazo, prevê desafios e oportunidades. Ideal para gestão de projetos e desenvolvimento organizacional.",
  "Executor": "Orientado à ação, proativo, determinado, foco em resultados tangíveis. Ideal para vendas, operações e gestão de projetos.",
  "Analista": "Abordagem lógica e orientada a dados. Detalhista, metódico, forte análise crítica. Ideal para finanças, pesquisa e TI.",
  "Facilitador": "Trabalho em equipe e colaboração, empático, atento, ouvinte. Mediador em conflitos. Ideal para RH, serviços sociais e educação.",
  "Empreendedor": "Iniciativa e apetite por risco, busca oportunidades, gera novos negócios e novas frentes.",
  "Competitivo": "Foco em superar metas e resultados, prospera em ambientes desafiadores e comparativos.",
};

function CheckboxGroup({ label, options, values, onChange, testid, descriptions }) {
  const [showDesc, setShowDesc] = useState(null);
  const toggle = (opt) => {
    const set = new Set(values || []);
    if (set.has(opt)) set.delete(opt); else set.add(opt);
    onChange(Array.from(set));
  };
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569] font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const checked = (values || []).includes(opt);
          const desc = descriptions?.[opt];
          return (
            <label key={opt} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] border cursor-pointer text-[12.5px] transition-colors ${checked ? "bg-[#e0e7ff] border-[#4338ca] text-[#4338ca]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(opt)} className="accent-[#4338ca]" />
              <span>{opt}</span>
              {desc && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowDesc(showDesc === opt ? null : opt); }}
                  className="text-[#94a3b8] hover:text-[#475569]"
                  title={desc}
                >
                  <AlertTriangle size={11} />
                </button>
              )}
            </label>
          );
        })}
      </div>
      {showDesc && descriptions?.[showDesc] && (
        <div className="mt-2 p-3 bg-[#f8fafc] border border-[#e2e8f0] rounded-[10px] text-[12px] text-[#475569] leading-relaxed">
          <strong className="text-[#0f172a]">{showDesc}:</strong> {descriptions[showDesc]}
        </div>
      )}
    </div>
  );
}

function MultiSelectWithCustom({ label, options, values, onChange, testid }) {
  const [custom, setCustom] = useState("");
  const toggle = (opt) => {
    const set = new Set(values || []);
    if (set.has(opt)) set.delete(opt); else set.add(opt);
    onChange(Array.from(set));
  };
  const addCustom = () => {
    if (custom.trim() && !(values || []).includes(custom.trim())) {
      onChange([...(values || []), custom.trim()]);
      setCustom("");
    }
  };
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569] font-semibold mb-2">{label}</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {options.map((opt) => {
          const checked = (values || []).includes(opt);
          return (
            <label key={opt} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] border cursor-pointer text-[12px] transition-colors ${checked ? "bg-[#f0fdf4] border-[#16a34a] text-[#166534]" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(opt)} className="accent-[#16a34a]" />
              {opt}
            </label>
          );
        })}
        {(values || []).filter((v) => !options.includes(v)).map((custom) => (
          <span key={custom} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] bg-[#fef3c7] border border-[#f59e0b] text-[#78350f] text-[12px]">
            {custom}
            <button type="button" onClick={() => toggle(custom)} className="hover:bg-[#f59e0b]/30 rounded-full p-0.5"><X size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} className="sw-input flex-1 !text-[12px] !py-1.5" placeholder="ex.: Excel, AutoCAD…" />
        <button type="button" onClick={addCustom} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5"><Plus size={12} /></button>
      </div>
    </div>
  );
}

function EquipmentChecklist({ available, values, onChange, testid, departments }) {
  const [custom, setCustom] = useState("");
  // Normalize `available` to array of {name, department_id}
  const normalize = (list) => (list || []).map((x) => typeof x === "string" ? { name: x, department_id: null } : x);
  const availableItems = normalize(available);
  const availableNames = availableItems.map((x) => x.name);
  const deptById = (departments || []).reduce((acc, d) => { acc[d.id] = d.name; return acc; }, {});
  const toggle = (name) => {
    const set = new Set(values || []);
    if (set.has(name)) set.delete(name); else set.add(name);
    onChange(Array.from(set));
  };
  const addCustom = () => {
    if (custom.trim() && !(values || []).includes(custom.trim())) {
      onChange([...(values || []), custom.trim()]);
      setCustom("");
    }
  };
  const allNames = Array.from(new Set([...availableNames, ...(values || [])]));
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mb-2">
        {allNames.map((name) => {
          const meta = availableItems.find((x) => x.name === name);
          const deptName = meta?.department_id ? deptById[meta.department_id] : null;
          return (
            <label key={name} className="inline-flex items-center gap-2 px-2 py-1.5 rounded-[8px] hover:bg-[#f8fafc] cursor-pointer text-[12.5px]">
              <input type="checkbox" checked={(values || []).includes(name)} onChange={() => toggle(name)} className="accent-[#475569]" />
              <span className="text-[#0f172a] flex-1">{name}</span>
              {deptName && (
                <span className="sw-badge sw-badge-neutral !text-[9.5px] !py-0" title="Departamento responsável">{deptName}</span>
              )}
            </label>
          );
        })}
      </div>
      <div className="flex gap-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} className="sw-input flex-1 !text-[12px] !py-1.5" placeholder="Adicionar equipamento…" />
        <button type="button" onClick={addCustom} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5"><Plus size={12} /></button>
      </div>
    </div>
  );
}

function EmployeeProfileListsEditor({ lists, departments, onClose, onSaved }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState({ ...lists });
  const [saving, setSaving] = useState(false);
  const dims = [
    { key: "behavioral", label: t("admin.dimension_behavioral") },
    { key: "education", label: t("admin.dimension_education") },
    { key: "experience", label: t("admin.dimension_experience") },
    { key: "seniority", label: t("admin.dimension_seniority") },
    { key: "base", label: t("admin.dimension_base") },
    { key: "equipment", label: t("admin.section_equipment") },
    { key: "hard_skills", label: t("admin.hard_skills") },
    { key: "soft_skills", label: t("admin.soft_skills") },
  ];
  const [active, setActive] = useState("behavioral");
  const [newItem, setNewItem] = useState("");

  const isEquipment = active === "equipment";
  const equipItems = (draft.equipment || []).map((x) => typeof x === "string" ? { name: x, department_id: null } : x);

  const addItem = () => {
    if (!newItem.trim()) return;
    if (isEquipment) {
      if (equipItems.some((x) => x.name === newItem.trim())) return;
      setDraft({ ...draft, equipment: [...equipItems, { name: newItem.trim(), department_id: null }] });
    } else {
      if ((draft[active] || []).includes(newItem.trim())) return;
      setDraft({ ...draft, [active]: [...(draft[active] || []), newItem.trim()] });
    }
    setNewItem("");
  };
  const removeItem = (item) => {
    if (isEquipment) {
      const name = typeof item === "string" ? item : item.name;
      setDraft({ ...draft, equipment: equipItems.filter((x) => x.name !== name) });
    } else {
      setDraft({ ...draft, [active]: (draft[active] || []).filter((x) => x !== item) });
    }
  };
  const setEquipDept = (name, deptId) => {
    setDraft({
      ...draft,
      equipment: equipItems.map((x) => x.name === name ? { ...x, department_id: deptId || null } : x),
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/employee-profile-lists", draft);
      onSaved(r.data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-[20px] max-w-[820px] w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-lists-modal">
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-[#f1f5f9]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.edit_lists")}</div>
            <h3 className="sw-heading text-[20px] mt-0.5">{t("admin.section_employee_profile")}</h3>
          </div>
          <button onClick={onClose} className="text-[#94a3b8] hover:text-[#475569]"><X size={20} /></button>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {dims.map((d) => (
              <button
                key={d.key}
                onClick={() => setActive(d.key)}
                data-testid={`dim-tab-${d.key}`}
                className={`px-3 py-1.5 rounded-[10px] text-[12px] font-medium transition-colors ${active === d.key ? "bg-[#0f172a] text-white" : "bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0]"}`}
              >{d.label}</button>
            ))}
          </div>
          {isEquipment && (
            <div className="text-[11.5px] text-[#94a3b8] mb-3 bg-[#f8fafc] rounded-[10px] px-3 py-2">
              {t("admin.equipment_dept_hint")}
            </div>
          )}
          <div className="flex flex-col gap-1.5 mb-3 max-h-[340px] overflow-y-auto">
            {isEquipment ? (
              equipItems.map((it) => (
                <div key={it.name} className="flex items-center gap-2 px-3 py-2 bg-[#f8fafc] rounded-[8px]" data-testid={`equip-row-${it.name}`}>
                  <span className="text-[13px] text-[#0f172a] flex-1">{it.name}</span>
                  <select
                    value={it.department_id || ""}
                    onChange={(e) => setEquipDept(it.name, e.target.value)}
                    className="sw-input !py-1 !text-[11.5px] max-w-[220px]"
                    data-testid={`equip-dept-${it.name}`}
                  >
                    <option value="">— {t("admin.responsible_dept")}</option>
                    {(departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <button onClick={() => removeItem(it)} className="text-[#881a27] hover:bg-[#e5c1c1] p-1 rounded-[6px]" data-testid={`equip-remove-${it.name}`}><Trash2 size={12} /></button>
                </div>
              ))
            ) : (
              (draft[active] || []).map((item) => (
                <div key={item} className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] rounded-[8px]">
                  <span className="text-[13px] text-[#0f172a]">{item}</span>
                  <button onClick={() => removeItem(item)} className="text-[#881a27] hover:bg-[#e5c1c1] p-1 rounded-[6px]"><Trash2 size={12} /></button>
                </div>
              ))
            )}
            {((isEquipment ? equipItems : (draft[active] || [])).length === 0) && <div className="text-[12px] text-[#94a3b8] italic p-3">Lista vazia</div>}
          </div>
          <div className="flex gap-2 mb-4">
            <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }} className="sw-input flex-1" placeholder={t("admin.new_item")} data-testid="new-list-item-input" />
            <button onClick={addItem} className="sw-btn-secondary" data-testid="add-list-item"><Plus size={14} /></button>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#f1f5f9]">
            <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button onClick={save} disabled={saving} className="sw-btn-primary" data-testid="save-lists-btn">{saving ? "..." : t("common.save")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobProfilesTab() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [accessProfiles, setAccessProfiles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [payrollCompanies, setPayrollCompanies] = useState([]);
  const [matrices, setMatrices] = useState([]);
  const [matrixCells, setMatrixCells] = useState({}); // { matrix_id: { cells, tracks, levels } }
  const [lists, setLists] = useState({
    behavioral: [], education: [], experience: [], seniority: [], base: [],
    equipment: [], hard_skills: [], soft_skills: [],
  });
  const [editing, setEditing] = useState(null);
  const [showListsEditor, setShowListsEditor] = useState(false);
  const empty = {
    name: "", description: "", department_id: "", job_title: "",
    payroll_companies: [], access_profile_id: "", equipment: [],
    payment_components_default: [], active: true,
    behavioral: [], education: [], experience: [], seniority: [], base: [],
    responsibilities: "", function_description: "",
    salary_matrix_id: "", salary_quadrants: [],
    hard_skills: [], soft_skills: [],
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [r, a, d, ru, ep, m] = await Promise.all([
      api.get("/job-profiles"),
      api.get("/access-profiles"),
      api.get("/departments"),
      api.get("/pto-rules").catch(() => ({ data: [] })),
      api.get("/admin/employee-profile-lists").catch(() => ({ data: null })),
      api.get("/salary-matrices").catch(() => ({ data: [] })),
    ]);
    setRows(r.data);
    setAccessProfiles(a.data);
    setDepartments(d.data);
    const pcs = Array.from(new Set((ru.data || []).map((x) => x.payroll_company).filter(Boolean))).sort();
    setPayrollCompanies(pcs);
    if (ep.data) setLists(ep.data);
    setMatrices(m.data || []);
  };
  useEffect(() => { load(); }, []);

  const loadMatrixCells = async (matrixId) => {
    if (!matrixId || matrixCells[matrixId]) return;
    try {
      const { data } = await api.get(`/salary-matrix?matrix_id=${matrixId}`);
      setMatrixCells((prev) => ({
        ...prev,
        [matrixId]: {
          cells: data.cells || [],
          tracks: data.tracks || [],
          levels: data.levels || [],
          currency: data.currency || "USD",
        },
      }));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (form.salary_matrix_id) loadMatrixCells(form.salary_matrix_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.salary_matrix_id]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        department_id: form.department_id || null,
        access_profile_id: form.access_profile_id || null,
        salary_matrix_id: form.salary_matrix_id || null,
      };
      if (editing === "new") await api.post("/job-profiles", payload);
      else await api.patch(`/job-profiles/${editing.id}`, payload);
      await load(); setEditing(null);
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => { if (window.confirm(t("common.delete_q"))) { await api.delete(`/job-profiles/${id}`); await load(); } };

  const togglePC = (pc) => {
    const set = new Set(form.payroll_companies || []);
    if (set.has(pc)) set.delete(pc); else set.add(pc);
    setForm({ ...form, payroll_companies: Array.from(set) });
  };

  const matrixData = matrixCells[form.salary_matrix_id] || { cells: [], tracks: [], levels: [], currency: "USD" };
  const currentCells = matrixData.cells;
  const cellByTrackLevel = useMemo(() => {
    const map = {};
    currentCells.forEach((c) => { map[`${c.track_id}::${c.level_id}`] = c; });
    return map;
  }, [currentCells]);
  const fmtBand = (min, max) => {
    const cur = matrixData.currency === "BRL" ? "R$" : "$";
    const k = (n) => n >= 1000 ? `${Math.round(n / 100) / 10}k` : n;
    if (!min && !max) return "—";
    return `${cur}${k(min || 0)}${max ? `–${k(max)}` : ""}`;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[12px] text-[#94a3b8]">{t("admin.multi_select_hint")}</div>
        <div className="flex gap-2">
          <button data-testid="edit-lists-btn" onClick={() => setShowListsEditor(true)} className="sw-btn-secondary"><Pencil size={12} /> {t("admin.edit_lists")}</button>
          <button data-testid="new-position-btn" onClick={() => { setForm(empty); setEditing("new"); }} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_position")}</button>
        </div>
      </div>

      {editing && (
        <form onSubmit={save} className="flex flex-col gap-4">
          {/* === Section 1: Position core === */}
          <div className="sw-card border-2 border-[#e2e8f0]">
            <div className="flex justify-between items-center mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{editing === "new" ? t("admin.new_position") : t("admin.edit_position")}</div>
                <h3 className="sw-heading text-[16px] mt-0.5">{t("admin.section_position_core")}</h3>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-[#94a3b8]"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="sw-label">{t("admin.position_name")} *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="position-name" /></div>
              <div><label className="sw-label">{t("admin.job_title")}</label><input value={form.job_title || ""} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="sw-input" /></div>
              <div><label className="sw-label">{t("common.department")}</label><select value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="sw-input"><option value="">—</option>{departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
              <div>
                <label className="sw-label">{t("admin.multi_payroll_companies")}</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {payrollCompanies.length === 0 && <span className="text-[11px] text-[#94a3b8] italic">{t("admin.no_payroll_companies")}</span>}
                  {payrollCompanies.map((pc) => {
                    const on = (form.payroll_companies || []).includes(pc);
                    return (
                      <button key={pc} type="button" onClick={() => togglePC(pc)} data-testid={`toggle-pc-${pc}`} className={`px-2.5 py-1 rounded-[8px] text-[12px] border transition-colors ${on ? "bg-[#e0e7ff] border-[#4338ca] text-[#4338ca] font-medium" : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8]"}`}>{pc}</button>
                    );
                  })}
                </div>
              </div>
              <div className="md:col-span-2"><label className="sw-label">{t("common.description")}</label><textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>
              <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />{t("common.active")}</label>
            </div>
          </div>

          {/* === Section 2: Access Profile === */}
          <div className="sw-card">
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.section_access_profile")}</div>
              <p className="text-[12px] text-[#94a3b8]">{t("admin.section_access_profile_desc")}</p>
            </div>
            <select value={form.access_profile_id} onChange={(e) => setForm({ ...form, access_profile_id: e.target.value })} className="sw-input" data-testid="position-access-profile">
              <option value="">—</option>
              {accessProfiles.filter((p) => p.active !== false).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          {/* === Section 3: Equipment === */}
          <div className="sw-card">
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.section_equipment")}</div>
              <p className="text-[12px] text-[#94a3b8]">{t("admin.equipment_hint")}</p>
            </div>
            <EquipmentChecklist testid="position-equipment" available={lists.equipment} values={form.equipment || []} onChange={(v) => setForm({ ...form, equipment: v })} departments={departments} />
          </div>

          {/* === Section 4: Employee Profile === */}
          <div className="sw-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.section_employee_profile")}</div>
                <p className="text-[12px] text-[#94a3b8]">{t("admin.section_employee_profile_desc")}</p>
              </div>
              <button type="button" onClick={() => setShowListsEditor(true)} className="text-[11px] text-[#475569] hover:underline"><Pencil size={10} className="inline" /> {t("admin.edit_lists")}</button>
            </div>
            <div className="flex flex-col gap-4">
              <CheckboxGroup testid="ep-behavioral" label={t("admin.dimension_behavioral")} options={lists.behavioral || []} values={form.behavioral || []} onChange={(v) => setForm({ ...form, behavioral: v })} descriptions={BEHAVIORAL_DESCRIPTIONS} />
              <CheckboxGroup testid="ep-education" label={t("admin.dimension_education")} options={lists.education || []} values={form.education || []} onChange={(v) => setForm({ ...form, education: v })} />
              <CheckboxGroup testid="ep-experience" label={t("admin.dimension_experience")} options={lists.experience || []} values={form.experience || []} onChange={(v) => setForm({ ...form, experience: v })} />
              <CheckboxGroup testid="ep-seniority" label={t("admin.dimension_seniority")} options={lists.seniority || []} values={form.seniority || []} onChange={(v) => setForm({ ...form, seniority: v })} />
              <CheckboxGroup testid="ep-base" label={t("admin.dimension_base")} options={lists.base || []} values={form.base || []} onChange={(v) => setForm({ ...form, base: v })} />
            </div>
          </div>

          {/* === Section 5: Job Profile === */}
          <div className="sw-card">
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#94a3b8] font-semibold">{t("admin.section_job_profile")}</div>
              <p className="text-[12px] text-[#94a3b8]">{t("admin.section_job_profile_desc")}</p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="sw-label">{t("admin.responsibilities")}</label>
                <p className="text-[11px] text-[#94a3b8] mb-1">{t("admin.responsibilities_hint")}</p>
                <textarea rows={3} value={form.responsibilities || ""} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} className="sw-input" data-testid="position-responsibilities" placeholder="- Bater metas de..." />
              </div>
              <div>
                <label className="sw-label">{t("admin.function_desc")}</label>
                <p className="text-[11px] text-[#94a3b8] mb-1">{t("admin.function_desc_hint")}</p>
                <textarea rows={3} value={form.function_description || ""} onChange={(e) => setForm({ ...form, function_description: e.target.value })} className="sw-input" data-testid="position-function" placeholder="- Executar..." />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="sw-label">{t("admin.salary_matrix")}</label>
                  <select value={form.salary_matrix_id || ""} onChange={(e) => setForm({ ...form, salary_matrix_id: e.target.value, salary_quadrants: [] })} className="sw-input" data-testid="position-salary-matrix">
                    <option value="">{t("admin.salary_matrix_none")}</option>
                    {matrices.filter((m) => m.active !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              {form.salary_matrix_id && currentCells.length > 0 && (
                <div>
                  <label className="sw-label">{t("admin.salary_quadrants")}</label>
                  <p className="text-[11px] text-[#94a3b8] mb-2">{t("admin.salary_quadrants_hint")}</p>
                  <div className="overflow-x-auto border border-[#e2e8f0] rounded-[10px]" data-testid="salary-quadrants-matrix">
                    <table className="text-[11.5px] w-full">
                      <thead className="bg-[#f8fafc]">
                        <tr>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold sticky left-0 bg-[#f8fafc]">{t("admin.matrix_track")}</th>
                          {(matrixData.levels || []).map((lv) => (
                            <th key={lv.id} className="text-center px-2 py-2 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold min-w-[100px]">
                              {lv.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(matrixData.tracks || []).map((tr) => (
                          <tr key={tr.id} className="border-t border-[#f1f5f9]">
                            <td className="px-3 py-2 font-medium text-[#0f172a] sticky left-0 bg-white whitespace-nowrap">{tr.name}</td>
                            {(matrixData.levels || []).map((lv) => {
                              const cell = cellByTrackLevel[`${tr.id}::${lv.id}`];
                              if (!cell) {
                                return <td key={lv.id} className="px-2 py-2 bg-[#fafafa]"><span className="text-[10px] text-[#cbd5e1]">—</span></td>;
                              }
                              const on = (form.salary_quadrants || []).includes(cell.id);
                              return (
                                <td key={lv.id} className="px-1 py-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const set = new Set(form.salary_quadrants || []);
                                      if (set.has(cell.id)) set.delete(cell.id); else set.add(cell.id);
                                      setForm({ ...form, salary_quadrants: Array.from(set) });
                                    }}
                                    data-testid={`quadrant-${cell.id}`}
                                    className={`w-full px-2 py-2 rounded-[8px] border transition-all text-center ${
                                      on
                                        ? "bg-[#e0e7ff] border-[#4338ca] text-[#4338ca] font-semibold shadow-sm"
                                        : "bg-white border-[#e2e8f0] text-[#475569] hover:border-[#94a3b8] hover:bg-[#f8fafc]"
                                    }`}
                                  >
                                    <div className="text-[11.5px] font-medium">{fmtBand(cell.salary_min, cell.salary_max)}</div>
                                    {on && <div className="text-[9px] mt-0.5">✓ {t("admin.selected")}</div>}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[11px] text-[#94a3b8] mt-1.5">
                    {t("admin.quadrants_selected", { n: (form.salary_quadrants || []).length })}
                  </p>
                </div>
              )}
              <MultiSelectWithCustom testid="position-hard-skills" label={t("admin.hard_skills")} options={lists.hard_skills || []} values={form.hard_skills || []} onChange={(v) => setForm({ ...form, hard_skills: v })} />
              <MultiSelectWithCustom testid="position-soft-skills" label={t("admin.soft_skills")} options={lists.soft_skills || []} values={form.soft_skills || []} onChange={(v) => setForm({ ...form, soft_skills: v })} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setEditing(null)} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" className="sw-btn-primary" data-testid="save-position-btn"><Save size={13} /> {t("common.save")}</button>
          </div>
        </form>
      )}

      {!editing && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {rows.map((r) => {
            const ap = accessProfiles.find((p) => p.id === r.access_profile_id);
            const dept = departments.find((d) => d.id === r.department_id);
            const pcs = r.payroll_companies || [];
            return (
              <div key={r.id} className="sw-card" data-testid={`position-card-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#475569]">
                      {pcs.length > 0 ? pcs.join(" · ") : (r.contract_type?.replace("_", "·") || "—")}
                      {ap ? ` · ${ap.name}` : ""}
                    </div>
                    <div className="font-medium text-[16px] text-[#323232] mt-1">{r.name}</div>
                    {dept && <div className="text-[12px] text-[#747474]">{dept.name}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setForm({ ...empty, ...r, department_id: r.department_id || "", access_profile_id: r.access_profile_id || "", salary_matrix_id: r.salary_matrix_id || "" }); setEditing(r); }} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5" data-testid={`edit-position-${r.id}`}>{t("common.edit")}</button>
                    <button onClick={() => remove(r.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1.5 rounded-[10px] text-[12px] font-medium">{t("common.delete")}</button>
                  </div>
                </div>
                {r.description && <p className="text-[12px] text-[#747474] mb-3">{r.description}</p>}
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  {r.behavioral?.length > 0 && (
                    <div><span className="text-[#94a3b8] uppercase tracking-wider">{t("admin.dimension_behavioral")}:</span> <span className="text-[#475569]">{r.behavioral.join(", ")}</span></div>
                  )}
                  {r.seniority?.length > 0 && (
                    <div><span className="text-[#94a3b8] uppercase tracking-wider">{t("admin.dimension_seniority")}:</span> <span className="text-[#475569]">{r.seniority.join(", ")}</span></div>
                  )}
                  {r.hard_skills?.length > 0 && (
                    <div className="col-span-2"><span className="text-[#94a3b8] uppercase tracking-wider">{t("admin.hard_skills")}:</span> <span className="text-[#475569]">{r.hard_skills.slice(0, 4).join(", ")}{r.hard_skills.length > 4 ? ` +${r.hard_skills.length - 4}` : ""}</span></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showListsEditor && (
        <EmployeeProfileListsEditor lists={lists} departments={departments} onClose={() => setShowListsEditor(false)} onSaved={(newLists) => { setLists(newLists); setShowListsEditor(false); }} />
      )}
    </div>
  );
}

function PaymentEditorModal({ user, onClose, onSaved }) {
  const { t } = useI18n();
  const [components, setComponents] = useState(user.payment_components || []);
  const [saving, setSaving] = useState(false);

  const addComp = () => setComponents([...components, { format: "hourly", amount: 0, label: "" }]);
  const updateComp = (idx, field, value) => {
    const c = [...components];
    c[idx] = { ...c[idx], [field]: field === "amount" ? parseFloat(value) || 0 : value };
    setComponents(c);
  };
  const removeComp = (idx) => setComponents(components.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { payment_components: components });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const presets = [
    { label: "Hourly only", value: [{ format: "hourly", amount: 65, label: "Hourly rate" }] },
    { label: "Daily rate", value: [{ format: "daily", amount: 500, label: "Daily rate" }] },
    { label: "Monthly salary", value: [{ format: "monthly", amount: 5000, label: "Base salary" }] },
    { label: "Composite (hourly + monthly)", value: [{ format: "hourly", amount: 60, label: "Field rate" }, { format: "monthly", amount: 500, label: "Bonus" }] },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[640px] w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="sw-heading text-[20px]">Payment configuration</h3>
            <p className="text-[13px] text-[#747474] mt-1">{user.name} · {user.contract_type?.replace("_", " · ")}</p>
          </div>
          <button onClick={onClose} className="text-[#747474] hover:text-[#323232]"><X size={20} /></button>
        </div>

        <div className="mb-4">
          <div className="sw-label">Quick presets</div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button key={p.label} type="button" onClick={() => setComponents(p.value)} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5">{p.label}</button>
            ))}
          </div>
        </div>

        <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2">Components</div>
        <div className="flex flex-col gap-3 mb-4">
          {components.length === 0 && <div className="text-[13px] text-[#747474] py-4 text-center bg-[#f6f6f6] rounded-[10px]">{t("admin.no_components")}</div>}
          {components.map((c, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-[140px_120px_1fr_40px] gap-2 items-end bg-[#f6f6f6] p-3 rounded-[12px]">
              <div>
                <label className="sw-label">Format</label>
                <select value={c.format} onChange={(e) => updateComp(idx, "format", e.target.value)} className="sw-input">
                  <option value="hourly">Per hour</option>
                  <option value="daily">Per day</option>
                  <option value="monthly">Per month</option>
                  <option value="fixed">Fixed/period</option>
                </select>
              </div>
              <div>
                <label className="sw-label">{t("admin.amount_usd")}</label>
                <input data-testid={`comp-amount-${idx}`} type="number" step="0.01" value={c.amount} onChange={(e) => updateComp(idx, "amount", e.target.value)} className="sw-input" />
              </div>
              <div>
                <label className="sw-label">Label / description</label>
                <input value={c.label || ""} onChange={(e) => updateComp(idx, "label", e.target.value)} className="sw-input" placeholder="e.g. Base salary" />
              </div>
              <button type="button" onClick={() => removeComp(idx)} className="text-[#881a27] hover:bg-[#e5c1c1] p-2 rounded-[10px] h-fit">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center">
          <button type="button" onClick={addComp} className="sw-btn-secondary"><Plus size={14} /> {t("admin.add_component")}</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="save-payment-btn" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Travel Policies Tab ============
function TravelPoliciesTab() {
  const { t } = useI18n();
  const [policies, setPolicies] = useState([]);
  const [openId, setOpenId] = useState(null); // currently-expanded policy id ('new' for the create form)
  const [newDraft, setNewDraft] = useState(null);

  const load = async () => {
    const r = await api.get("/travel-policies");
    setPolicies(r.data);
    // Auto-expand the active policy on first load — usually the only one a company cares about
    if (r.data.length > 0 && openId === null) {
      const active = r.data.find((p) => p.active) || r.data[0];
      setOpenId(active.id);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const activate = async (p) => {
    await api.patch(`/travel-policies/${p.id}`, { active: true });
    load();
  };

  const startNew = () => {
    setNewDraft({
      name: "", description: "", currency: "USD", active: true,
      categories: [
        { category: "meals", label: "Meals", daily_limit: 75, unit: "per_day", enforcement_mode: "per_trip", notes: "" },
        { category: "lodging", label: "Lodging", daily_limit: 200, unit: "per_night", enforcement_mode: "per_day", notes: "" },
        { category: "transport", label: "Transport", daily_limit: 100, unit: "per_day", enforcement_mode: "per_day", notes: "" },
        { category: "mileage", label: "Mileage", daily_limit: 0.67, unit: "per_mile", enforcement_mode: "per_item", notes: "" },
        { category: "per_diem", label: "Per diem", daily_limit: 50, unit: "per_day", enforcement_mode: "per_day", notes: "" },
        { category: "other", label: "Other", daily_limit: 100, unit: "per_trip", enforcement_mode: "per_trip", notes: "" },
      ],
    });
    setOpenId("new");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="text-[13px] text-[#747474] max-w-[640px]">{t("admin.policy_subtitle")}</div>
        <button data-testid="new-travel-policy-btn" onClick={startNew} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_policy")}</button>
      </div>

      {/* Inline new-policy editor */}
      {openId === "new" && newDraft && (
        <PolicyAccordionRow
          isNew
          policy={newDraft}
          isOpen
          onToggle={() => { setOpenId(null); setNewDraft(null); }}
          onSaved={() => { setOpenId(null); setNewDraft(null); load(); }}
        />
      )}

      {/* Existing policies — accordion list */}
      <div className="flex flex-col gap-2">
        {policies.length === 0 && openId !== "new" && (
          <div className="sw-card text-center text-[13px] text-[#94a3b8] py-8 italic">
            {t("admin.no_policies")}
          </div>
        )}
        {policies.map((p) => (
          <PolicyAccordionRow
            key={p.id}
            policy={p}
            isOpen={openId === p.id}
            onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            onActivate={() => activate(p)}
            onSaved={() => load()}
          />
        ))}
      </div>
    </div>
  );
}

function PolicyAccordionRow({ policy, isOpen, isNew = false, onToggle, onActivate, onSaved }) {
  const { t } = useI18n();
  // Local draft so toggling Open/Close doesn't lose unsaved edits
  const [form, setForm] = useState(policy);
  const [saving, setSaving] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null); // category index currently expanded
  const [qboAccounts, setQboAccounts] = useState([]); // synced QBO accounts (chart of accounts)
  useEffect(() => { setForm(policy); }, [policy]);
  useEffect(() => {
    if (!isOpen) return;
    api.get("/qbo/catalog/categories")
      .then(({ data }) => {
        const items = (data?.items || []).filter((a) => (data?.selected_ids || []).length === 0 || (data?.selected_ids || []).includes(String(a.Id)));
        // Sort hierarchically: prefer FullyQualifiedName if available
        items.sort((a, b) => String(a.FullyQualifiedName || a.Name || "").localeCompare(String(b.FullyQualifiedName || b.Name || "")));
        setQboAccounts(items);
      })
      .catch(() => setQboAccounts([]));
  }, [isOpen]);

  const catCount = (form.categories || []).length;

  const updateCat = (i, patch) =>
    setForm({ ...form, categories: form.categories.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  const addCat = () => {
    const taken = new Set((form.categories || []).map((c) => (c.category || "").toLowerCase()));
    let key = "new_type"; let n = 2;
    while (taken.has(key)) { key = `new_type_${n}`; n += 1; }
    setForm({
      ...form,
      categories: [
        ...(form.categories || []),
        { category: key, label: "", daily_limit: 0, unit: "per_day", enforcement_mode: "per_day", notes: "" },
      ],
    });
    setExpandedCat((form.categories || []).length); // auto-expand the new row
  };

  const removeCat = (i) => {
    const c = form.categories[i];
    if (!window.confirm(t("admin.policy_delete_type_confirm", { cat: c.category }))) return;
    setForm({ ...form, categories: form.categories.filter((_, idx) => idx !== i) });
    setExpandedCat(null);
  };

  const save = async () => {
    const keys = (form.categories || []).map((c) => (c.category || "").trim().toLowerCase());
    if (keys.some((k) => !k)) { alert("Todo tipo precisa de uma chave (ex: meals, lodging)."); return; }
    const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
    if (dupes.length) { alert(`Chave duplicada: ${dupes.join(", ")}. Cada tipo precisa ter chave única.`); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        categories: form.categories.map((c) => ({
          ...c,
          category: (c.category || "").trim().toLowerCase().replace(/\s+/g, "_"),
          label: (c.label || "").trim() || null,
          daily_limit: (c.daily_limit === null || c.daily_limit === "" || c.daily_limit === undefined) ? null : (parseFloat(c.daily_limit) || 0),
        })),
      };
      if (isNew || !form.id) await api.post("/travel-policies", payload);
      else await api.patch(`/travel-policies/${form.id}`, payload);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div className={`sw-card !p-0 overflow-hidden ${policy.active ? "border-[#475569] !border-2" : ""}`} data-testid={`policy-accordion-${form.id || "new"}`}>
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#f8fafc] transition-colors"
        data-testid={`policy-toggle-${form.id || "new"}`}
      >
        {isOpen ? <ChevronDown size={16} className="text-[#475569]" /> : <ChevronRight size={16} className="text-[#475569]" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[15px] text-[#1e293b] truncate">{form.name || (isNew ? t("admin.new_policy") : "—")}</span>
            {policy.active && <span className="sw-badge sw-badge-approved">ACTIVE</span>}
            <span className="text-[10.5px] uppercase tracking-wider text-[#94a3b8]">{form.currency}</span>
          </div>
          {form.description && <div className="text-[12px] text-[#747474] mt-0.5 truncate">{form.description}</div>}
          <div className="text-[11px] text-[#94a3b8] mt-1">{catCount} tipo{catCount === 1 ? "" : "s"} de despesa</div>
        </div>
        {!policy.active && !isNew && (
          <span onClick={(e) => { e.stopPropagation(); onActivate(); }} className="sw-btn-secondary !text-[11px] !px-2 !py-1 cursor-pointer">Ativar</span>
        )}
      </button>

      {/* Body */}
      {isOpen && (
        <div className="p-4 border-t border-[#e2e8f0] bg-[#fafbfc]">
          {/* Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="md:col-span-2">
              <label className="sw-label">Nome *</label>
              <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="policy-name" />
            </div>
            <div>
              <label className="sw-label">{t("common.currency")}</label>
              <input value={form.currency || "USD"} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="sw-input" />
            </div>
            <div className="md:col-span-2">
              <label className="sw-label">{t("common.description")}</label>
              <textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" />
            </div>
            <label className="flex items-end gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />
              <span>Definir como ativa (desativa outras)</span>
            </label>
          </div>

          {/* Categories header */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.12em] text-[#475569] font-semibold">Categorias / Tipos de despesa</div>
            <button
              type="button"
              data-testid="add-category-btn"
              onClick={addCat}
              className="sw-btn-secondary !text-[11px] !py-1 !px-2"
            >
              <Plus size={12} /> Adicionar tipo
            </button>
          </div>
          <div className="text-[11px] text-[#94a3b8] mb-3">
            💡 A chave (ex: <code className="bg-white px-1 py-0.5 rounded">meals</code>) é usada internamente nos lançamentos e no mapping QBO.
            O label é o que o integrante vê.
          </div>

          {/* Categories — each row is collapsible to keep things tidy with many types */}
          <div className="flex flex-col gap-2">
            {(form.categories || []).map((c, i) => {
              const open = expandedCat === i;
              const tiers = c.tier_limits || {};
              const setTier = (role, val) => {
                const next = { ...(c.tier_limits || {}) };
                if (val === "" || val == null) delete next[role];
                else next[role] = parseFloat(val);
                updateCat(i, { tier_limits: Object.keys(next).length ? next : null });
              };
              return (
                <div key={i} className="rounded-[10px] border border-[#e2e8f0] bg-white overflow-hidden" data-testid={`policy-cat-row-${i}`}>
                  {/* Summary header */}
                  <div className="flex items-center gap-2 p-2.5">
                    <button type="button" onClick={() => setExpandedCat(open ? null : i)} className="text-[#94a3b8] hover:text-[#1e293b] shrink-0" data-testid={`policy-cat-toggle-${i}`}>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-4 font-mono text-[12.5px] text-[#1e293b] truncate" title={c.category}>{c.label || c.category}</span>
                      <span className="col-span-2 text-[12px] text-[#475569] font-semibold">
                        {c.daily_limit === null || c.daily_limit === "" || c.daily_limit === undefined ? <em className="text-[#94a3b8] font-normal">sem limite</em> : `$${parseFloat(c.daily_limit) || 0}`}
                      </span>
                      <span className="col-span-2 text-[10.5px] uppercase tracking-wider text-[#94a3b8]">{(c.unit || "per_day").replace("_", "/")}</span>
                      <span className="col-span-3 text-[10.5px] text-[#475569] truncate">
                        {(c.enforcement_mode === "per_item" && "por item") ||
                         (c.enforcement_mode === "per_trip" && "por viagem (× dias)") ||
                         "por dia"}
                      </span>
                      <span className="col-span-1 text-right">
                        <button
                          type="button"
                          onClick={() => removeCat(i)}
                          className="text-[#881a27] hover:bg-[#fee2e2] rounded p-1.5 transition inline-flex items-center justify-center"
                          title={t("admin.delete_type")}
                          data-testid={`policy-cat-delete-${i}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </div>
                  </div>

                  {/* Expanded body — generous spacing */}
                  {open && (
                    <div className="px-4 pb-4 pt-2 border-t border-[#f1f5f9] flex flex-col gap-3 bg-[#fafbfc]">
                      {/* Row 1: key + label */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="sw-label">{t("admin.internal_key")}</label>
                          <input
                            data-testid={`policy-cat-key-${i}`}
                            value={c.category || ""}
                            onChange={(e) => updateCat(i, { category: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                            placeholder="ex: meals"
                            className="sw-input font-mono"
                          />
                        </div>
                        <div>
                          <label className="sw-label">Label exibido</label>
                          <input
                            data-testid={`policy-cat-label-${i}`}
                            value={c.label || ""}
                            onChange={(e) => updateCat(i, { label: e.target.value })}
                            placeholder={t("admin.cat_label_ph")}
                            className="sw-input"
                          />
                        </div>
                      </div>

                      {/* Row 2: limit + unit + enforcement */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="sw-label">Limite base ($)</label>
                          <div className="flex items-center gap-2 mb-1.5">
                            <input
                              type="checkbox"
                              id={`policy-nolimit-${c.category}`}
                              data-testid={`policy-nolimit-${c.category}`}
                              checked={c.daily_limit === null || c.daily_limit === "" || c.daily_limit === undefined}
                              onChange={(e) => updateCat(i, { daily_limit: e.target.checked ? null : 0 })}
                              className="accent-[#475569]"
                            />
                            <label htmlFor={`policy-nolimit-${c.category}`} className="text-[11px] text-[#475569] cursor-pointer">
                              Sem limite (não aplicar política)
                            </label>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            value={c.daily_limit ?? ""}
                            placeholder={c.daily_limit === null ? "— sem política —" : ""}
                            disabled={c.daily_limit === null || c.daily_limit === undefined}
                            onChange={(e) => updateCat(i, { daily_limit: e.target.value })}
                            className={`sw-input !py-2 !text-[15px] font-semibold text-right ${c.daily_limit === null ? "bg-[#f6f6f6] text-[#94a3b8] italic" : ""}`}
                            data-testid={`policy-limit-${c.category}`}
                          />
                        </div>
                        <div>
                          <label className="sw-label">Unidade (display)</label>
                          <select value={c.unit} onChange={(e) => updateCat(i, { unit: e.target.value })} className="sw-input">
                            <option value="per_day">per day</option>
                            <option value="per_night">per night</option>
                            <option value="per_mile">per mile</option>
                            <option value="per_trip">per trip</option>
                          </select>
                        </div>
                        <div>
                          <label className="sw-label">{t("admin.calc_mode")}</label>
                          <select
                            value={c.enforcement_mode || "per_day"}
                            onChange={(e) => updateCat(i, { enforcement_mode: e.target.value })}
                            className="sw-input"
                            data-testid={`policy-enforcement-${c.category}`}
                          >
                            <option value="per_item">{t("admin.calc_per_item")}</option>
                            <option value="per_day">{t("admin.calc_per_day")}</option>
                            <option value="per_trip">{t("admin.calc_per_trip")}</option>
                          </select>
                        </div>
                      </div>

                      {/* Row 3: QBO Chart of Accounts mapping */}
                      <div>
                        <label className="sw-label">{t("admin.policy_qbo_account")}</label>
                        <select
                          value={c.qbo_account_id || ""}
                          onChange={(e) => updateCat(i, { qbo_account_id: e.target.value || null })}
                          className="sw-input"
                          data-testid={`policy-qbo-account-${c.category}`}
                          disabled={qboAccounts.length === 0}
                        >
                          <option value="">{qboAccounts.length === 0 ? t("admin.policy_qbo_no_accounts") : t("qbo.not_mapped")}</option>
                          {qboAccounts.map((a) => (
                            <option key={a.Id} value={a.Id}>
                              {a.FullyQualifiedName || a.Name}{a.AccountType ? ` · ${a.AccountType}` : ""}
                            </option>
                          ))}
                        </select>
                        <p className="text-[11px] text-[#94a3b8] mt-1">{t("admin.policy_qbo_account_hint")}</p>
                      </div>

                      {/* Hint */}
                      <div className="text-[11.5px] text-[#475569] bg-[#f6f6f6] rounded-[8px] px-3 py-2">
                        {(c.daily_limit === null || c.daily_limit === "" || c.daily_limit === undefined) && (
                          <em className="text-[#94a3b8]">Nenhum limite será aplicado a esta categoria — despesas passam livremente.</em>
                        )}
                        {c.daily_limit !== null && c.daily_limit !== "" && c.daily_limit !== undefined && c.enforcement_mode === "per_item" && <>Cada lançamento deve ser ≤ <strong>${c.daily_limit || 0}</strong>.</>}
                        {c.daily_limit !== null && c.daily_limit !== "" && c.daily_limit !== undefined && (c.enforcement_mode === "per_day" || !c.enforcement_mode) && <>Soma do dia para esta categoria ≤ <strong>${c.daily_limit || 0}</strong>.</>}
                        {c.daily_limit !== null && c.daily_limit !== "" && c.daily_limit !== undefined && c.enforcement_mode === "per_trip" && <>Soma total da categoria ≤ <strong>${c.daily_limit || 0} × dias da viagem</strong> (ex.: 10 dias → cap ${(parseFloat(c.daily_limit) || 0) * 10}).</>}
                      </div>

                      {/* Notes — full width */}
                      <div>
                        <label className="sw-label">Notas (opcional)</label>
                        <textarea
                          rows={2}
                          value={c.notes || ""}
                          onChange={(e) => updateCat(i, { notes: e.target.value })}
                          placeholder={t("admin.policy_notes_ph")}
                          className="sw-input"
                        />
                      </div>

                      {/* Tier overrides — full row with comfortable inputs */}
                      <div className="border-t border-[#f1f5f9] pt-3">
                        <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold mb-2">Tier overrides — limite específico por papel (vazio = usa o base)</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {["ceo", "hr", "manager", "employee"].map((role) => (
                            <div key={role}>
                              <label className="sw-label uppercase">{role}</label>
                              <input
                                type="number"
                                step="0.01"
                                data-testid={`policy-tier-${c.category}-${role}`}
                                value={tiers[role] ?? ""}
                                onChange={(e) => setTier(role, e.target.value)}
                                className="sw-input !py-2 !text-[14px] text-right"
                                placeholder="—"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer — Save / Activate / Close */}
          <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[#e2e8f0]">
            <button type="button" onClick={onToggle} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button
              data-testid="save-travel-policy-btn"
              type="button"
              onClick={save}
              disabled={saving}
              className="sw-btn-primary disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Salvando…" : (isNew ? "Criar policy" : "Salvar alterações")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ Approval Chains Tab ============
function ApprovalChainsTab() {
  const { t } = useI18n();
  const [chains, setChains] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    const r = await api.get("/approval-chains");
    setChains(r.data);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="text-[13px] text-[#747474]">Parametrize approval tiers per process. Each tier defines max amount and ordered roles to approve.</div>
        <button data-testid="new-approval-chain-btn" onClick={() => setEditing({ process_type: "travel", name: "", active: true, tiers: [{ up_to_amount: 2000, roles: ["manager", "hr"] }, { up_to_amount: null, roles: ["manager", "hr", "ceo"] }] })} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_chain")}</button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {chains.map((c) => (
          <div key={c.id} className={`sw-card ${c.active ? "border-[#475569] border-2" : ""}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#475569]">{c.process_type} {c.active && "· ACTIVE"}</div>
                <div className="font-medium text-[16px] text-[#323232] mt-1">{c.name}</div>
              </div>
              <button onClick={() => setEditing(c)} className="sw-btn-secondary !text-[11px] !px-2 !py-1">{t("common.edit")}</button>
            </div>
            <div className="flex flex-col gap-2">
              {c.tiers?.map((tier, i) => (
                <div key={i} className="flex items-center gap-2 text-[12px] bg-[#f6f6f6] rounded-[8px] p-2">
                  <span className="text-[#747474]">{tier.up_to_amount === null ? "Above" : `Up to $${tier.up_to_amount}`}:</span>
                  {tier.roles.map((r, idx) => (
                    <React.Fragment key={idx}>
                      <span className="px-2 py-0.5 rounded-[6px] bg-[#e2e8f0] text-[#475569] capitalize font-medium">{r}</span>
                      {idx < tier.roles.length - 1 && <span className="text-[#747474]">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {editing && <ApprovalChainModal chain={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ApprovalChainModal({ chain, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(chain);
  const [saving, setSaving] = useState(false);
  const updateTier = (i, patch) => setForm({ ...form, tiers: form.tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { up_to_amount: null, roles: ["hr"] }] });
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });
  const toggleRole = (i, role) => {
    const cur = form.tiers[i].roles;
    const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
    updateTier(i, { roles: next });
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tiers: form.tiers.map((t) => ({ ...t, up_to_amount: t.up_to_amount === "" || t.up_to_amount === null ? null : parseFloat(t.up_to_amount) })) };
      if (chain.id) await api.patch(`/approval-chains/${chain.id}`, payload);
      else await api.post("/approval-chains", payload);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const ALL_ROLES = ["manager", "hr", "ceo", "admin"];
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[680px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="sw-heading text-[18px]">{chain.id ? "Edit" : "New"} Approval Chain</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div><label className="sw-label">Process *</label>
            <select value={form.process_type} onChange={(e) => setForm({ ...form, process_type: e.target.value })} className="sw-input" disabled={!!chain.id}>
              <option value="travel">Travel</option><option value="loan">Loan</option><option value="reimbursement_overhead">Reimb Overhead</option><option value="reimbursement_project">Reimb Project</option>
            </select>
          </div>
          <div><label className="sw-label">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" data-testid="chain-name" /></div>
          <label className="md:col-span-2 flex items-center gap-2 text-[13px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#475569]" />Set as active (deactivates other chains for the same process)</label>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-[#475569] mb-2 flex justify-between items-center">{t("admin.tiers")} <button onClick={addTier} className="text-[#475569] text-[10px] normal-case"><Plus size={10} className="inline" /> {t("common.add")}</button></div>
        <div className="flex flex-col gap-3 mb-4">
          {form.tiers.map((tier, i) => (
            <div key={i} className="p-3 rounded-[10px] bg-[#f6f6f6] border border-[#eeeded]">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] text-[#747474]">Up to amount ($):</span>
                <input type="number" placeholder={t("admin.unlimited_ph")} value={tier.up_to_amount ?? ""} onChange={(e) => updateTier(i, { up_to_amount: e.target.value === "" ? null : e.target.value })} className="sw-input !py-1 !text-[12px] flex-1" />
                {form.tiers.length > 1 && <button onClick={() => removeTier(i)} className="text-[#881a27]"><Trash2 size={14} /></button>}
              </div>
              <div className="flex flex-wrap gap-2">
                {ALL_ROLES.map((r) => (
                  <button key={r} onClick={() => toggleRole(i, r)} className={`px-3 py-1 rounded-[8px] text-[11px] capitalize ${tier.roles.includes(r) ? "bg-[#475569] text-white" : "bg-white border border-[#eeeded] text-[#747474]"}`}>{tier.roles.includes(r) && `${tier.roles.indexOf(r) + 1}. `}{r}</button>
                ))}
              </div>
              <div className="text-[10px] text-[#747474] mt-1">Click to toggle. Order = sequence of approvals.</div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
          <button data-testid="save-chain-btn" onClick={save} disabled={saving} className="sw-btn-primary">{saving ? "..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ============ Demo & Training Manual ============
function DemoTab() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("sw_token");

  const reseed = async () => {
    if (!window.confirm("This will WIPE all transactional data (PTO, Reimbursements, Travel, Loans, Hiring, Performance, Sage chat, etc.) and load a fresh demo state. Users, departments, projects, KB and trainings remain. Proceed?")) return;
    setLoading(true);
    try {
      const r = await api.post("/admin/seed-demo");
      setResult(r.data);
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };

  const download = async (fmt) => {
    try {
      const resp = await fetch(`${API}/api/admin/training-manual.${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("HTTP " + resp.status);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Sagewood_Training_Manual.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { alert(t("common.download_failed") + " " + err.message); }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="sw-card !p-6 bg-gradient-to-br from-[#475569] to-[#334155] text-white">
        <div className="flex items-center gap-3 mb-2"><Sparkles size={24} /><h3 className="text-[20px] font-semibold">Demo & Training Toolkit</h3></div>
        <p className="text-[13px] opacity-90">Prepare the portal for a client demo, training session, or internal walkthrough in one click. Reload a curated dataset, and download the official Training Manual to share before any presentation.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="sw-card">
          <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-[#475569]"><RefreshCw size={12} /> {t("admin.demo_reset")}</div>
          <h4 className="font-medium text-[16px] text-[#323232]">Reload rich demo data</h4>
          <p className="text-[13px] text-[#747474] mt-2 mb-4">Wipes transactional collections (PTO, reimbursements, travel, loans, terminations, hiring, performance, training assignments, Sage chat) and loads a fresh, curated &ldquo;showroom state&rdquo;:</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-[#475569] mb-4">
            <span>· 2 PTO requests</span><span>· 2 Reimbursements</span>
            <span>· 1 Travel (flagged)</span><span>· 1 Active loan</span>
            <span>· 2 Vacancies + 3 Applicants</span><span>· 1 Termination in progress</span>
            <span>· 1 Eval cycle + 2 Evaluations</span><span>· 1 Active PIP</span>
            <span>· 9 Training assignments</span><span>· 1 Verified Q&A + 1 Gap</span>
          </div>
          <button data-testid="seed-demo-btn" onClick={reseed} disabled={loading} className="sw-btn-primary !bg-[#475569]"><RefreshCw size={14} /> {loading ? "Loading..." : "Reload demo data"}</button>
          {result?.summary && <div className="mt-3 p-3 rounded-[10px] bg-[#e2e8f0] text-[11px] text-[#475569]">✓ Reloaded. {Object.entries(result.summary).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>}
        </div>

        <div className="sw-card">
          <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-[#475569]"><FileTextIcon size={12} /> Official Training Manual</div>
          <h4 className="font-medium text-[16px] text-[#323232]">{t("admin.slide_deck")}</h4>
          <p className="text-[13px] text-[#747474] mt-2 mb-4">A 26-slide manual combining demonstration, onboarding training, and policy explanation. Use it in the kick-off, share with new hires, or print as reference.</p>
          <div className="text-[11px] text-[#747474] mb-4 leading-relaxed">
            Includes: overview, roles, first access, dashboard, all modules (PTO, Reimb, Travel, Loans, Performance, PIP, Trainings, KB, Sage Copilot, Hiring, Offboarding, Admin), Smart Alerts, integrations, security, 3 step-by-step tutorials, FAQ, support contacts.
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="download-manual-pptx-btn" onClick={() => download("pptx")} className="sw-btn-primary"><Download size={14} /> {t("admin.download_pptx")}</button>
            <button data-testid="download-manual-pdf-btn" onClick={() => download("pdf")} className="sw-btn-secondary"><Download size={14} /> {t("admin.download_pdf")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Menu Visibility Tab ============
function MenuVisibilityTab() {
  const { t } = useI18n();
  const [data, setData] = useState({ items: [], roles: [], hidden: {} });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/nav-visibility");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = (key, role) => {
    setData((prev) => {
      const next = { ...prev, hidden: { ...prev.hidden } };
      const current = new Set(next.hidden[key] || []);
      if (current.has(role)) current.delete(role);
      else current.add(role);
      if (current.size === 0) delete next.hidden[key];
      else next.hidden[key] = Array.from(current);
      return next;
    });
  };

  const setAllForKey = (key, action) => {
    setData((prev) => {
      const next = { ...prev, hidden: { ...prev.hidden } };
      if (action === "hide-all") next.hidden[key] = [...prev.roles];
      else if (action === "show-all") delete next.hidden[key];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/nav-visibility", { hidden: data.hidden });
      setSavedAt(new Date());
      // notify user that they may need to refresh other browser tabs
    } catch (err) {
      alert(err?.response?.data?.detail || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = {
    employee: t("admin.role_employee"),
    manager: t("admin.role_manager"),
    ceo: t("admin.role_ceo"),
    hr: t("admin.role_hr"),
  };

  // Group items by section
  const sections = {};
  for (const it of data.items) {
    if (!sections[it.section]) sections[it.section] = [];
    sections[it.section].push(it);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="sw-card bg-[#f8fafc] border border-[#e2e8f0]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-white border border-[#e2e8f0] flex items-center justify-center text-[#475569] shrink-0">
            <EyeOff size={18} />
          </div>
          <div className="text-[13px] text-[#475569] leading-relaxed">
            <strong className="text-[#1e293b]">{t("admin.menuvis_desc")}</strong> {t("admin.menuvis_check")} <em>{t("admin.menuvis_hide")}</em> {t("admin.menuvis_hint")} <strong>{t("admin.menuvis_admin_note")}</strong> {t("admin.menuvis_reload")}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-[#747474]">
          {savedAt && <span className="text-[#16a34a]">✓ {t("admin.saved_at", { time: savedAt.toLocaleTimeString() })}</span>}
        </div>
        <button data-testid="save-nav-visibility-btn" onClick={save} disabled={saving} className="sw-btn-primary">
          <Save size={14} /> {saving ? t("admin.saving") : t("admin.save_config")}
        </button>
      </div>

      {Object.entries(sections).map(([section, items]) => (
        <div key={section} className="sw-card !p-0 overflow-hidden">
          <div className="bg-[#f6f6f6] px-5 py-3 text-[11px] uppercase tracking-wider text-[#475569] font-semibold">
            {section}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-white border-b border-[#eeeded] text-[#747474] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 w-[280px]">{t("admin.column_menu_item")}</th>
                  {data.roles.map((r) => (
                    <th key={r} className="text-center px-3 py-3">
                      {t("admin.column_hide_for", { role: roleLabel[r] || r })}
                    </th>
                  ))}
                  <th className="text-right px-5 py-3 w-[180px]">{t("admin.column_shortcuts")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const hiddenFor = new Set(data.hidden[it.key] || []);
                  const allHidden = data.roles.every((r) => hiddenFor.has(r));
                  return (
                    <tr key={it.key} data-testid={`nav-row-${it.key}`} className="border-t border-[#eeeded] hover:bg-[#fafafa]">
                      <td className="px-5 py-3">
                        <div className="font-medium text-[#1e293b]">{it.label}</div>
                        <div className="text-[11px] text-[#94a3b8] font-mono">{it.key}</div>
                      </td>
                      {data.roles.map((r) => (
                        <td key={r} className="px-3 py-3 text-center">
                          <input
                            data-testid={`toggle-${it.key}-${r}`}
                            type="checkbox"
                            checked={hiddenFor.has(r)}
                            onChange={() => toggle(it.key, r)}
                            className="w-4 h-4 accent-[#881a27] cursor-pointer"
                          />
                        </td>
                      ))}
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => setAllForKey(it.key, allHidden ? "show-all" : "hide-all")}
                          className="text-[11px] text-[#475569] hover:bg-[#e2e8f0] px-2 py-1 rounded-[8px]"
                          data-testid={`bulk-${it.key}`}
                        >
                          {allHidden ? <><Eye size={11} className="inline mr-1" />{t("admin.show_all")}</> : <><EyeOff size={11} className="inline mr-1" />{t("admin.hide_all")}</>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="flex justify-end pt-2">
        <button onClick={save} disabled={saving} className="sw-btn-primary">
          <Save size={14} /> {saving ? t("admin.saving") : t("admin.save_config")}
        </button>
      </div>
    </div>
  );
}


function ProjectEditorModal({ project, onClose, onSaved }) {
  const { t } = useI18n();
  const isEdit = !!project?.id;
  const [form, setForm] = useState({
    code: project?.code || "",
    name: project?.name || "",
    status: project?.status || "active",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/projects/${project.id}`, form);
      } else {
        await api.post("/projects", form);
      }
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="project-modal" className="bg-white rounded-[20px] max-w-[460px] w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[18px]">{isEdit ? t("admin.edit_project") : t("admin.new_project")}</h3>
          <button onClick={onClose} className="text-[#94a3b8]"><X size={20} /></button>
        </div>
        {err && <div className="rounded-[10px] border border-rose-200 bg-rose-50 text-rose-700 text-[12px] p-3 mb-3">{err}</div>}
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div>
            <label className="sw-label">Code *</label>
            <input data-testid="project-code" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="sw-input" placeholder="e.g. PROJ-2026-A1" />
          </div>
          <div>
            <label className="sw-label">Name *</label>
            <input data-testid="project-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("common.status")}</label>
            <select data-testid="project-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="sw-input">
              <option value="active">{t("common.active")}</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button data-testid="project-save" type="submit" disabled={saving} className="sw-btn-primary">{saving ? "Saving…" : (isEdit ? "Save" : "Create")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectsTab({ projects, reload }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(null);

  const handleDelete = async (p) => {
    if (!window.confirm(t("admin.project_delete_confirm", { code: p.code, name: p.name }))) return;
    setBusy(p.id);
    try {
      await api.delete(`/projects/${p.id}`);
      await reload();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const handleExport = () => {
    const rows = projects.map((p) => ({
      Code: p.code,
      Name: p.name,
      Status: p.status,
      "Reim counter": p.reim_counter || 0,
    }));
    const date = new Date().toISOString().slice(0, 10);
    if (rows.length === 0) { alert(t("common.nothing_export")); return; }
    const lines = [Object.keys(rows[0]).join(",")];
    rows.forEach((r) => lines.push(Object.values(r).map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `projects_${date}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        <button data-testid="projects-export-csv" onClick={handleExport} className="sw-btn-secondary"><Download size={14} /> {t("common.export_csv")}</button>
        <button data-testid="new-project-btn" onClick={() => setShowNew(true)} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_project")}</button>
      </div>
      <div className="sw-card !p-0 overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-[#f6f6f6] text-[#747474] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Code</th>
              <th className="text-left px-5 py-3">{t("common.name")}</th>
              <th className="text-left px-5 py-3">{t("common.status")}</th>
              <th className="text-left px-5 py-3">Reim counter</th>
              <th className="text-right px-5 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-[#94a3b8]">{t("admin.no_projects")}</td></tr>
            )}
            {projects.map((p) => (
              <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-t border-[#eeeded]">
                <td className="px-5 py-3 font-medium text-[#475569]">{p.code}</td>
                <td className="px-5 py-3">{p.name}</td>
                <td className="px-5 py-3">
                  <span className={`sw-badge ${p.status === "active" ? "sw-badge-approved" : "sw-badge-neutral"}`}>{p.status}</span>
                </td>
                <td className="px-5 py-3">{p.reim_counter || 0}</td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`project-edit-${p.id}`} onClick={() => setEditing(p)} className="text-[#475569] hover:text-[#1e293b] text-[12px] mr-3">{t("common.edit")}</button>
                  <button
                    data-testid={`project-delete-${p.id}`}
                    onClick={() => handleDelete(p)}
                    disabled={busy === p.id}
                    className="text-rose-600 hover:text-rose-800 text-[12px] inline-flex items-center gap-1"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <ProjectEditorModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      {editing && <ProjectEditorModal project={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}



function CollapsibleSection({ id, title, subtitle, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sw-card !p-0 overflow-hidden" data-testid={testid || `section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`section-toggle-${id}`}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#f8fafc] hover:bg-[#f1f5f9] transition-colors border-b border-[#eeeded]"
      >
        <div className="text-left">
          <div className="font-medium text-[15px] text-[#0f172a]">{title}</div>
          {subtitle && <div className="text-[12px] text-[#94a3b8] mt-0.5">{subtitle}</div>}
        </div>
        {open ? <ChevronDown size={18} className="text-[#475569]" /> : <ChevronRight size={18} className="text-[#475569]" />}
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

function DepartmentsSection({ departments, reload }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(null);
  const empty = { name: "", cost_code: "", parent_id: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (d) => { setForm({ ...empty, ...d, parent_id: d.parent_id || "" }); setEditing(d); };
  const cancel = () => { setEditing(null); setForm(empty); };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (editing === "new") await api.post("/departments", payload);
      else await api.patch(`/departments/${editing.id}`, payload);
      await reload();
      cancel();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm(t("common.delete_q"))) return;
    try {
      await api.delete(`/departments/${id}`);
      await reload();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <button data-testid="new-dept-btn" onClick={startNew} className="sw-btn-primary"><Plus size={14} /> {t("admin.new_dept")}</button>
      </div>
      {editing && (
        <form onSubmit={save} className="sw-card grid grid-cols-1 md:grid-cols-3 gap-4 border-2 border-[#e2e8f0]">
          <div className="md:col-span-3 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{editing === "new" ? t("admin.new_dept") : t("admin.edit_dept")}</div>
            <button type="button" onClick={cancel} className="text-[#747474]"><X size={18} /></button>
          </div>
          <div>
            <label className="sw-label">{t("common.name")} *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Cost code *</label>
            <input required value={form.cost_code} onChange={(e) => setForm({ ...form, cost_code: e.target.value })} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.parent_dept")}</label>
            <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="sw-input">
              <option value="">—</option>
              {(departments || []).filter((d) => d.id !== editing?.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={cancel} className="sw-btn-secondary">{t("common.cancel")}</button>
            <button type="submit" disabled={saving} className="sw-btn-primary">{saving ? "..." : t("common.save")}</button>
          </div>
        </form>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(departments || []).map((d) => (
          <div key={d.id} className="sw-card" data-testid={`dept-card-${d.id}`}>
            <div className="text-[11px] uppercase tracking-wider text-[#475569]">{d.cost_code}</div>
            <div className="font-medium text-[16px] text-[#323232] mt-1">{d.name}</div>
            {d.parent_id && <div className="text-[12px] text-[#747474] mt-1">Parent: {(departments || []).find((x) => x.id === d.parent_id)?.name}</div>}
            <div className="flex gap-1 mt-3">
              <button onClick={() => startEdit(d)} className="sw-btn-secondary !text-[12px] !px-3 !py-1.5">{t("common.edit")}</button>
              <button onClick={() => remove(d.id)} className="text-[#881a27] hover:bg-[#e5c1c1] px-3 py-1.5 rounded-[10px] text-[12px] font-medium">{t("common.delete")}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QBOAdminTab() {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-4">
      <div className="sw-card bg-[#f8fafc] border border-[#e2e8f0]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-white border border-[#e2e8f0] flex items-center justify-center text-[#475569] shrink-0">
            <Database size={18} />
          </div>
          <div className="text-[13px] text-[#475569] leading-relaxed">
            <strong className="text-[#1e293b]">{t("admin.tab_qbo_admin")}.</strong> {t("admin.qbo_admin_desc")}
          </div>
        </div>
      </div>
      <QBOMapping embedded />
    </div>
  );
}


export default function Admin() {
  const { t } = useI18n();
  const [tab, setTab] = useState("pto-mgmt");
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [integrations, setIntegrations] = useState({});

  const loadCore = async () => {
    const [u, d, p, w, fc, ds] = await Promise.all([
      api.get("/users"),
      api.get("/departments"),
      api.get("/projects"),
      api.get("/workflows"),
      api.get("/integrations/frankcrum/status"),
      api.get("/integrations/docusign/status"),
    ]);
    setUsers(u.data);
    setDepts(d.data);
    setProjects(p.data);
    setWorkflows(w.data);
    setIntegrations({ frankcrum: fc.data, docusign: ds.data });
  };
  useEffect(() => { loadCore(); }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center">
            <Settings size={22} />
          </div>
          <h1 className="sw-heading text-[28px]">{t("admin.title")}</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 bg-white border border-[#eeeded] rounded-[20px] p-2 w-fit overflow-x-auto max-w-full">
        <Tab active={tab === "pto-mgmt"} onClick={() => setTab("pto-mgmt")} icon={CalendarClock} label={t("admin.pto_mgmt")} testid="tab-admin-pto-mgmt" />
        <Tab active={tab === "pto-rules"} onClick={() => setTab("pto-rules")} icon={BookOpen} label={t("admin.pto_rules")} testid="tab-admin-pto-rules" />
        <Tab active={tab === "qbo-admin"} onClick={() => setTab("qbo-admin")} icon={Database} label={t("admin.tab_qbo_admin")} testid="tab-admin-qbo-admin" />
        <Tab active={tab === "users"} onClick={() => setTab("users")} icon={UsersIcon} label={t("admin.users")} testid="tab-admin-users" />
        <Tab active={tab === "job-profiles"} onClick={() => setTab("job-profiles")} icon={IdCard} label={t("admin.tab_positions")} testid="tab-admin-positions" />
        <Tab active={tab === "access-profiles"} onClick={() => setTab("access-profiles")} icon={Key} label={t("admin.tab_access_profiles")} testid="tab-admin-access-profiles" />
        <Tab active={tab === "projects"} onClick={() => setTab("projects")} icon={Briefcase} label={t("admin.projects")} testid="tab-admin-projects" />
        <Tab active={tab === "approval-chains"} onClick={() => setTab("approval-chains")} icon={Workflow} label={t("admin.tab_workflows")} testid="tab-admin-approval-chains" />
        <Tab active={tab === "travel-policies"} onClick={() => setTab("travel-policies")} icon={Plane} label={t("admin.tab_travel_policy")} testid="tab-admin-travel-policies" />
        <Tab active={tab === "notifications"} onClick={() => setTab("notifications")} icon={Bell} label={t("admin.tab_notifications")} testid="tab-admin-notifications" />
        <Tab active={tab === "menu-visibility"} onClick={() => setTab("menu-visibility")} icon={EyeOff} label={t("admin.tab_menu_visibility")} testid="tab-admin-menu-visibility" />
        <Tab active={tab === "integrations"} onClick={() => setTab("integrations")} icon={Plug} label={t("admin.integrations")} testid="tab-admin-integrations" />
        <Tab active={tab === "deploy-check"} onClick={() => setTab("deploy-check")} icon={ShieldCheck} label={t("admin.tab_deploy_check")} testid="tab-admin-deploy-check" />
        <Tab active={tab === "permissions"} onClick={() => setTab("permissions")} icon={Key} label={t("admin.tab_permissions")} testid="tab-admin-permissions" />
      </div>

      {tab === "pto-mgmt" && <PTOManagementTab />}
      {tab === "pto-rules" && <PTORulesTab />}
      {tab === "qbo-admin" && <QBOAdminTab />}

      {tab === "users" && <UsersTab />}
      {tab === "job-profiles" && <JobProfilesTab />}
      {tab === "access-profiles" && <AccessProfilesTab />}
      {tab === "approval-chains" && <ApprovalChainsTab />}
      {tab === "travel-policies" && <TravelPoliciesTab />}
      {tab === "notifications" && <NotificationRulesTab />}
      {tab === "menu-visibility" && <MenuVisibilityTab />}
      {tab === "deploy-check" && <PredeployCheck />}
      {tab === "permissions" && <PermissionsMatrix />}

      {tab === "projects" && <ProjectsTab projects={projects} reload={loadCore} />}

      {tab === "workflows" && null}

      {tab === "integrations" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(integrations).map(([key, val]) => (
            <div key={key} className="sw-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#475569]">{key}</div>
                  <div className="font-medium text-[16px] text-[#323232] mt-1 capitalize">{key === "frankcrum" ? "FrankCrum (Payroll)" : "DocuSign (IAM)"}</div>
                </div>
                <span className="sw-badge sw-badge-pending">{val.mode}</span>
              </div>
              <p className="text-[13px] text-[#747474] mt-3">{val.message}</p>
              <button className="sw-btn-secondary mt-4" disabled>{t("admin.configure_soon")}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationRulesTab() {
  const { t } = useI18n();
  const [rules, setRules] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [showOutbox, setShowOutbox] = useState(false);

  const load = async () => {
    const r = await api.get("/notification-rules");
    setRules(r.data);
    setDraft(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        training_reminder_days: draft.training_reminder_days,
        training_overdue_escalate_manager_days: parseInt(draft.training_overdue_escalate_manager_days),
        training_overdue_escalate_hr_days: parseInt(draft.training_overdue_escalate_hr_days),
        pto_pending_reminder_hours: parseInt(draft.pto_pending_reminder_hours),
        evaluation_pending_reminder_days: parseInt(draft.evaluation_pending_reminder_days),
        follow_up_90_enabled: !!draft.follow_up_90_enabled,
        termination_reminder_days: parseInt(draft.termination_reminder_days),
        send_emails: !!draft.send_emails,
      };
      const r = await api.patch("/notification-rules", body);
      setRules(r.data);
      setDraft(r.data);
      alert("Notification rules saved.");
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const runDryRun = async () => {
    setRunning(true);
    try {
      const r = await api.post("/notifications/run-now?dry_run=true");
      setRunResult(r.data);
    } catch (err) { alert(err?.response?.data?.detail || "Error"); }
    finally { setRunning(false); }
  };

  const loadOutbox = async () => {
    const r = await api.get("/notifications/outbox");
    setOutbox(r.data);
    setShowOutbox(true);
  };

  if (!draft) return <div className="text-[14px] text-[#747474]">Loading…</div>;

  const updateField = (k, v) => setDraft({ ...draft, [k]: v });
  const days = draft.training_reminder_days || [];

  return (
    <div className="flex flex-col gap-5 max-w-[820px]">
      <div className="sw-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="sw-heading text-[18px]">{t("admin.corp_notif_rules")}</h2>
            <p className="text-[12px] text-[#747474] mt-1">{t("admin.corp_notif_desc")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">{t("admin.email_transport")}</span>
            <span className={`text-[11px] px-2 py-1 rounded-full ${draft.send_emails ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
              {draft.send_emails ? t("admin.enabled_stub") : t("admin.disabled_label")}
            </span>
          </div>
        </div>

        <div className="rounded-[10px] border border-amber-200 bg-amber-50 text-amber-800 text-[12px] p-3 mb-5">
          <strong>{t("admin.email_note_title")}</strong> {t("admin.email_note")}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="sw-label">Training reminder days (CSV before due)</label>
            <input
              data-testid="rules-training-reminder-days"
              value={days.join(",")}
              onChange={(e) => updateField("training_reminder_days", e.target.value.split(",").map((x) => parseInt(x.trim())).filter((x) => !isNaN(x)))}
              className="sw-input"
              placeholder="7,3,1"
            />
          </div>
          <div>
            <label className="sw-label">Overdue → escalate to manager after (days)</label>
            <input data-testid="rules-esc-mgr" type="number" min="1" value={draft.training_overdue_escalate_manager_days} onChange={(e) => updateField("training_overdue_escalate_manager_days", e.target.value)} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Overdue → escalate to HR after (days)</label>
            <input data-testid="rules-esc-hr" type="number" min="1" value={draft.training_overdue_escalate_hr_days} onChange={(e) => updateField("training_overdue_escalate_hr_days", e.target.value)} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.pto_reminder_hours")}</label>
            <input data-testid="rules-pto-hours" type="number" min="1" value={draft.pto_pending_reminder_hours} onChange={(e) => updateField("pto_pending_reminder_hours", e.target.value)} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">{t("admin.eval_reminder_days")}</label>
            <input data-testid="rules-eval-days" type="number" min="1" value={draft.evaluation_pending_reminder_days} onChange={(e) => updateField("evaluation_pending_reminder_days", e.target.value)} className="sw-input" />
          </div>
          <div>
            <label className="sw-label">Termination reminder (days before)</label>
            <input data-testid="rules-term-days" type="number" min="0" value={draft.termination_reminder_days} onChange={(e) => updateField("termination_reminder_days", e.target.value)} className="sw-input" />
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#e2e8f0]">
          <label className="flex items-center gap-2 text-[13px] text-[#1e293b] cursor-pointer">
            <input type="checkbox" data-testid="rules-followup-90" checked={!!draft.follow_up_90_enabled} onChange={(e) => updateField("follow_up_90_enabled", e.target.checked)} />
            90-day follow-up notification (to HR)
          </label>
          <label className="flex items-center gap-2 text-[13px] text-[#1e293b] cursor-pointer">
            <input type="checkbox" data-testid="rules-send-emails" checked={!!draft.send_emails} onChange={(e) => updateField("send_emails", e.target.checked)} />
            Master switch: send emails (stub outbox until provider wired)
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button data-testid="rules-run-dry" onClick={runDryRun} disabled={running} className="sw-btn-secondary"><Play size={13} /> {running ? "Running…" : "Dry-run now"}</button>
          <button data-testid="rules-outbox-btn" onClick={loadOutbox} className="sw-btn-secondary"><Bell size={13} /> {t("admin.view_outbox")}</button>
          <button data-testid="rules-save" onClick={save} disabled={saving} className="sw-btn-primary"><Save size={13} /> {saving ? "Saving…" : "Save rules"}</button>
        </div>
      </div>

      {runResult && (
        <div className="sw-card">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569] mb-2">Dry-run result</div>
          <p className="text-[13px] text-[#475569]"><strong>{runResult.computed}</strong> {t("admin.would_send")} <strong>{runResult.send_emails_enabled ? t("common.yes") : t("common.no")}</strong>.</p>
          {runResult.sample?.length > 0 && (
            <div className="mt-3 flex flex-col gap-2 max-h-[280px] overflow-y-auto">
              {runResult.sample.map((s, i) => (
                <div key={i} className="text-[12px] border border-[#e2e8f0] rounded-[10px] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#475569]">{s.kind}</div>
                  <div className="font-medium text-[#1e293b] mt-1">{s.subject}</div>
                  <div className="text-[#94a3b8] text-[11px]">to: {s.to}</div>
                  <div className="text-[12px] text-[#475569] mt-2 whitespace-pre-line">{s.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showOutbox && (
        <div className="sw-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#475569]">Recent outbox · {outbox.length}</div>
            <button onClick={() => setShowOutbox(false)} className="text-[#94a3b8]"><X size={14} /></button>
          </div>
          {outbox.length === 0 ? (
            <div className="text-[12px] text-[#94a3b8] text-center py-4">{t("admin.outbox_empty")}</div>
          ) : (
            <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
              {outbox.map((o) => (
                <div key={o.id} className="text-[12px] border border-[#e2e8f0] rounded-[10px] p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-[#475569]">{o.kind}</div>
                    <div className="text-[10px] text-[#94a3b8]">{o.created_at?.slice(0,16).replace("T"," ")}</div>
                  </div>
                  <div className="font-medium text-[#1e293b] mt-1">{o.subject}</div>
                  <div className="text-[#94a3b8] text-[11px]">to: {o.to} · status: {o.status}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
